import React, { useState, useEffect, useRef, useId } from 'react';
import { Link } from 'react-router-dom';
import { Button, Modal, Form, Alert, OverlayTrigger, Tooltip } from 'react-bootstrap';
import {
  getTickerShields,
  addTickerShield,
  updateTickerShield,
  deleteTickerShield,
  deleteAllTickerShields,
  updateAccount,
  commitShieldsToBroker
} from '../services/api';
import { subscribeToTickerUpdates } from '../services/socketService';
import {
  DEFAULT_NEW_POSITION_SHIELD_PRESET,
  DEFAULT_NEW_POSITION_SHIELD_PRESET_OPTIONS,
  normalizeDefaultNewPositionShieldPreset
} from '../utils/defaultNewPositionShieldPreset';
const ARMOR_DEBUG_ENABLED = false;
const armorDebugLog = (...args) => {
  if (ARMOR_DEBUG_ENABLED) {
    console.log(...args);
  }
};

const ArmorEdit = ({ 
  accountId, 
  tickerId, 
  currentArmor, 
  onArmorUpdate, 
  onShieldUpdate, 
  inline = false,
  customCostBasis = null,
  customShares = null,
  transactionId = null,
  tradeId = null,
  isSoldOut = false,
  isClosed = false,
  soldPrice = null, 
  closedRightContent = null, 
  shields: providedShields = null, 
  tickerInfo: providedTickerInfo = null,
  hitPrices = null,
  enableRealtimeUpdates = true,
  // Inline (per-lot) instances fetch shields scoped to just that lot, so they can never see
  // an overall shield in their own `shields` state -- TradeDetails.js computes this from the
  // whole trade's shield list and passes it in instead. See the derived `hasOverallShield`
  // below for how the non-inline (aggregate modal) case is handled.
  hasOverallShield: hasOverallShieldProp = false
}) => {
  // Helper function to format shield names for display
  const formatShieldName = (shieldName) => {
    if (shieldName && shieldName.includes('Trailing Stop')) {
      return shieldName.replace('Trailing Stop', 'Trailing');
    }
    if (shieldName && shieldName.includes('Stop Loss')) {
      return shieldName.replace('Stop Loss', 'Stop');
    }
    return shieldName;
  };

  // Unique per-instance prefix for DOM ids/names. TradeDetails renders one
  // ArmorEdit per purchase-price lot (inline mode) and TickerTable renders one
  // per row, so hardcoded ids/names would collide across simultaneously
  // mounted instances -- the browser groups same-`name` radios and resolves
  // `htmlFor` to the first same-`id` element in the whole document, which
  // silently breaks the checked/highlight state on every instance after the
  // first. See the "Price for Calculation" radio group below.
  const instanceId = useId();

  const [showModal, setShowModal] = useState(false);
  const [shields, setShields] = useState([]);
  const [tickerInfo, setTickerInfo] = useState(null);
  const [accountInfo, setAccountInfo] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [defaultPresetSaving, setDefaultPresetSaving] = useState(false);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingShield, setEditingShield] = useState(null);
  const [newShield, setNewShield] = useState({
    shield_name: 'Stop Loss',
    shield_price: '',
    shield_type: 'PRICE',
    shield_shares: '',
    trailing_distance: '',
    trailing_distance_type: 'PERCENTAGE'
  });
  const [rowCommitLoadingId, setRowCommitLoadingId] = useState(null);
  const [priceCalculationMethod, setPriceCalculationMethod] = useState(
    () => localStorage.getItem('armorPriceCalculationMethod_v2') || 'trailing'
  );
  // Only meaningful in the aggregate Shields modal (non-inline): whether new
  // stops are created off the ticker's average cost basis across all shares
  // ('overall'), or the user is sent to Trade Details to set them up per buy
  // lot ('individual', the only option that existed before this toggle).
  const [stopsMode, setStopsMode] = useState('overall');
  const [defaultNewPositionShieldPreset, setDefaultNewPositionShieldPreset] = useState(DEFAULT_NEW_POSITION_SHIELD_PRESET);
  const [singleStopSizes, setSingleStopSizes] = useState({}); // Track size selection for each stop percentage (default 100%)
  const [trailingStopSizes, setTrailingStopSizes] = useState({}); // Track size for trailing stop buttons
 // Track distribution patterns for all-in buttons
  const [sizeSelectionModal, setSizeSelectionModal] = useState({ show: false, type: null, key: null }); // Track which button is being configured
  const latestLoadRequestRef = useRef(0);
  // Guards the one-time stopsMode auto-select in loadShields below, so it only runs off the
  // first successful shield load and never yanks the toggle away from a choice the user makes
  // afterward (e.g. on a websocket-triggered refresh while the modal is still open).
  const stopsModeAutoSelectedRef = useRef(false);

  // Whether an overall (buy_transaction_id-less) shield already exists for this ticker+account.
  // Inline instances can't see this in their own lot-scoped `shields` state, so they're told via
  // the `hasOverallShield` prop; the aggregate modal (non-inline) loads every shield for the
  // ticker+account unscoped, so it can derive it directly.
  const hasOverallShield = inline
    ? hasOverallShieldProp
    : shields.some(shield => !shield.buy_transaction_id);

  // The reverse signal: whether a per-lot shield already exists for this ticker+account.
  // Aggregate-modal-only, same reasoning as hasOverallShield above -- an inline instance's
  // own shields are all per-lot by construction (scoped to its own transactionId), so this
  // wouldn't mean anything there. `stopsMode` still *defaults* to 'overall' even when only
  // per-lot shields exist, so this has to gate stop creation itself, not just the toggle --
  // otherwise a user landing on the default 'overall' tab could create a mixed overall stop
  // before ever touching the toggle.
  const hasPerLotShield = !inline && shields.some(shield => shield.buy_transaction_id);

  // Load shields when component mounts and when modal opens
  // loadShields intentionally omitted: it closes over `loading` to guard against
  // concurrent loads, so memoizing it would change identity on every load and
  // could re-trigger this effect mid-request.
  useEffect(() => {
    loadShields();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tickerId, accountId, providedShields, providedTickerInfo, hitPrices]);

  useEffect(() => {
    if (showModal) {
      loadShields();
      triggerShieldRefresh();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showModal]);

  useEffect(() => {
    setDefaultNewPositionShieldPreset(
      normalizeDefaultNewPositionShieldPreset(accountInfo?.default_new_position_shield_preset)
    );
  }, [accountInfo?.default_new_position_shield_preset]);

  useEffect(() => {
    localStorage.setItem('armorPriceCalculationMethod_v2', priceCalculationMethod);
  }, [priceCalculationMethod]);

  // Subscribe to WebSocket ticker updates to refresh shields when prices change
  useEffect(() => {
    if (!enableRealtimeUpdates) {
      return;
    }

    const unsubscribe = subscribeToTickerUpdates((data) => {
      // Check if this ticker was updated
      if (data.updated_tickers && data.updated_tickers.length > 0) {
        const tickerUpdated = data.updated_tickers.some(ticker => 
          ticker.symbol === tickerInfo?.symbol || ticker.ticker_id === tickerId
        );
        
        if (tickerUpdated) {
          armorDebugLog(`[ArmorEdit] Ticker ${tickerInfo?.symbol || tickerId} updated via WebSocket, refreshing shields`);
          loadShields();
        }
      }
    });

    return unsubscribe;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tickerId, tickerInfo?.symbol, enableRealtimeUpdates]);

  const loadShields = async (options = {}) => {
    const { force = false, skipLoadingState = false } = options;

    if (loading && !force) {
      return;
    }

    const requestId = Date.now();
    latestLoadRequestRef.current = requestId;
    const shouldToggleLoading = !skipLoadingState;

    if (shouldToggleLoading) {
      setLoading(true);
    }

    armorDebugLog(`[ArmorEdit] Starting loadShields for ticker ${tickerId}, accountId ${accountId}`);

    try {
      let resolvedShields = [];
      let resolvedTickerInfo = null;
      let resolvedAccountInfo = null;

      // Use provided data if available (for closed trades), otherwise fetch from API
      if (providedShields && providedTickerInfo) {
        armorDebugLog(`[ArmorEdit] Using provided shields data for closed trade:`, providedShields);
        resolvedShields = providedShields;
        resolvedTickerInfo = providedTickerInfo;
        resolvedAccountInfo = { account_id: accountId, name: 'Trade Account' };
      } else {
        const response = await getTickerShields(tickerId, accountId, transactionId);
        const loadedShields = response.data.shields || [];

        armorDebugLog(`[ArmorEdit] Loaded ${loadedShields.length} shields for ticker ${tickerId}:`, loadedShields);
        armorDebugLog(`[ArmorEdit] onArmorUpdate exists:`, !!onArmorUpdate);
        armorDebugLog(`[ArmorEdit] ticker_info exists:`, !!response.data.ticker_info);

        resolvedShields = loadedShields;
        resolvedTickerInfo = response.data.ticker_info || null;
        resolvedAccountInfo = response.data.account_info || null;
      }

      if (latestLoadRequestRef.current !== requestId) {
        return;
      }

      setShields(resolvedShields);
      setTickerInfo(resolvedTickerInfo);
      setAccountInfo(resolvedAccountInfo);

      // One-time on the first successful load: default the aggregate modal's stopsMode toggle
      // to whichever kind of stop is actually already in use, instead of always starting on
      // 'overall'. Only "per-lot stops exist and no overall stop exists" should flip this to
      // 'individual' -- overall-only, both, or neither all keep the pre-existing default.
      if (!inline && !stopsModeAutoSelectedRef.current) {
        stopsModeAutoSelectedRef.current = true;
        const loadedHasOverallShield = resolvedShields.some(shield => !shield.buy_transaction_id);
        const loadedHasPerLotShield = resolvedShields.some(shield => shield.buy_transaction_id);
        if (loadedHasPerLotShield && !loadedHasOverallShield) {
          setStopsMode('individual');
        }
      }

      const currentShields = resolvedShields;
      const currentTickerInfo = resolvedTickerInfo || tickerInfo || null;

      setError('');

      // Always notify parent when we have shield data, regardless of count
      if (onArmorUpdate && currentTickerInfo) {
        if (currentShields.length > 0) {
          // Calculate risk values (same logic as TickerTable)
          const totalPotentialPL = currentShields.reduce((total, shield) => {
            return total + (shield.amount_from_cost || 0);
          }, 0);

          const costBasisPositionValue = (currentTickerInfo?.cost_basis || 0) * (currentTickerInfo?.shares || 0);
          const overallEffectPercentage = costBasisPositionValue > 0 ? (totalPotentialPL / costBasisPositionValue) * 100 : 0;

          armorDebugLog(`[ArmorEdit] Calculated values for ticker ${tickerId}: totalPL=${totalPotentialPL}, effect=${overallEffectPercentage}%`);

          // Notify parent with shield data
          onArmorUpdate({
            ticker_id: tickerId,
            armor: 'shields',
            shield_risk_percentage: overallEffectPercentage,
            shield_risk_amount: totalPotentialPL
          });

          armorDebugLog(`[ArmorEdit] Notified parent for ticker ${tickerId} with shields`);
        } else {
          armorDebugLog(`[ArmorEdit] No shields found for ticker ${tickerId}, notifying parent with null values`);
          // Notify parent that there are no shields
          onArmorUpdate({
            ticker_id: tickerId,
            armor: 'No armor',
            shield_risk_percentage: null,
            shield_risk_amount: null
          });

          armorDebugLog(`[ArmorEdit] Notified parent for ticker ${tickerId} with no shields`);
        }
      } else {
        armorDebugLog(`[ArmorEdit] Not notifying parent for ticker ${tickerId} - onArmorUpdate: ${!!onArmorUpdate}, ticker_info: ${!!currentTickerInfo}`);
      }
    } catch (err) {
      if (latestLoadRequestRef.current !== requestId) {
        return;
      }

      console.error('Error loading shields:', err);
      setError('Failed to load shields');
      // Set empty shields and basic ticker info even on error
      setShields([]);
    } finally {
      if (latestLoadRequestRef.current === requestId && shouldToggleLoading) {
        setLoading(false);
      }
    }
  };

  const triggerShieldRefresh = () => {
    if (onShieldUpdate) {
      onShieldUpdate(tickerId);
    }
  };

  const handleShow = () => {
    setShowModal(true);
  };

  // Tags every created shield with this panel's transactionId (null for the aggregate,
  // whole-position view), so per-lot cards each read/write their own scoped shield list.
  const createShield = (shieldData) =>
    addTickerShield(tickerId, accountId, { ...shieldData, transaction_id: transactionId });
  
  const handleShowAddForm = () => {
    // Calculate available shares for new shield
    let availableShares = 0;
    if (tickerInfo) {
      const totalProtectedShares = shields.reduce((total, shield) => {
        return total + (parseFloat(shield.shield_shares) || 0);
      }, 0);
      const shares = customShares !== null ? customShares : tickerInfo.shares;
      availableShares = Math.max(0, shares - totalProtectedShares);
    }
    
    // Set default shield_shares to 100% of available shares
    setNewShield({
      shield_name: 'Stop Loss',
      shield_price: '',
      shield_type: 'PRICE',
      shield_shares: availableShares > 0 ? availableShares.toString() : '',
      trailing_distance: '',
      trailing_distance_type: 'PERCENTAGE'
    });
    
    setShowAddForm(!showAddForm);
  };

  // Helper function to check if all shares are protected and auto-navigate to table
  const checkAndAutoNavigateToTable = async () => {
    try {
      // Refetch shields data to ensure we have the latest data, not stale state
      const response = await getTickerShields(tickerId, accountId, transactionId);
      const currentShields = response.data.shields || [];

      const totalShares = customShares !== null ? customShares : (tickerInfo?.shares || 0);
      if (totalShares <= 0) return;

      // Calculate total protected shares from freshly fetched shields
      const totalProtectedShares = currentShields.reduce((total, shield) => total + (parseFloat(shield.shield_shares) || 0), 0);
      const protectionPercentage = (totalProtectedShares / totalShares) * 100;

      // If 100% or more shares are protected, automatically switch to table view
      if (protectionPercentage >= 100) {
        setShowAddForm(false);
      }
    } catch (err) {
      // If refetch fails, silently continue - don't interrupt the user workflow
      console.error('Failed to check protection level for auto-navigation:', err);
    }
  };

  const applyT2T5Preset = async () => {
    if (!tickerInfo) return;
    try {
      setShowAddForm(true);
      setLoading(true);
      const totalShares = customShares !== null ? customShares : (tickerInfo.shares || 0);
      const totalProtectedShares = shields.reduce((total, shield) => total + (parseFloat(shield.shield_shares) || 0), 0);
      const availableShares = Math.max(0, totalShares - totalProtectedShares);
      if (availableShares <= 0) { setError('No available shares to allocate'); return; }
      const currentPrice = tickerInfo.effective_price || tickerInfo.current_price || 0;
      if (!currentPrice || isNaN(currentPrice)) { setError('Missing current price to compute stops'); return; }
      const basePriceFixed = priceCalculationMethod === 'current'
        ? currentPrice
        : (customCostBasis !== null ? customCostBasis : (tickerInfo.cost_basis || currentPrice));
      const half = Math.floor(availableShares / 2);
      const trailingShares = half;
      const fixedShares = availableShares - half;
      if (trailingShares > 0) {
        await createShield({
          shield_name: 'T2%-Trim', shield_type: 'TRAILING_STOP',
          shield_percentage: trailingShares.toString(), is_active: true,
          shield_price: currentPrice, trailing_distance: 2,
          trailing_distance_type: 'PERCENTAGE'
        });
      }
      if (fixedShares > 0) {
        await createShield({
          shield_name: 'F5%-Exit', shield_type: 'PRICE',
          shield_percentage: fixedShares.toString(), is_active: true,
          shield_price: +(basePriceFixed * (1 - 5 / 100)).toFixed(4)
        });
      }
      await loadShields({ force: true, skipLoadingState: true });
      triggerShieldRefresh();
      setError('');
      checkAndAutoNavigateToTable();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to apply T2-F5 preset');
    } finally {
      setLoading(false);
    }
  };

  const applyT3T7Preset = async () => {
    if (!tickerInfo) return;
    try {
      setShowAddForm(true);
      setLoading(true);
      const totalShares = customShares !== null ? customShares : (tickerInfo.shares || 0);
      const totalProtectedShares = shields.reduce((total, shield) => total + (parseFloat(shield.shield_shares) || 0), 0);
      const availableShares = Math.max(0, totalShares - totalProtectedShares);
      if (availableShares <= 0) { setError('No available shares to allocate'); return; }
      const currentPrice = tickerInfo.effective_price || tickerInfo.current_price || 0;
      if (!currentPrice || isNaN(currentPrice)) { setError('Missing current price to compute stops'); return; }
      const basePriceFixed = priceCalculationMethod === 'current'
        ? currentPrice
        : (customCostBasis !== null ? customCostBasis : (tickerInfo.cost_basis || currentPrice));
      const half = Math.floor(availableShares / 2);
      const trailingShares = half;
      const fixedShares = availableShares - half;
      if (trailingShares > 0) {
        await createShield({
          shield_name: 'T3%-Trim', shield_type: 'TRAILING_STOP',
          shield_percentage: trailingShares.toString(), is_active: true,
          shield_price: currentPrice, trailing_distance: 3,
          trailing_distance_type: 'PERCENTAGE'
        });
      }
      if (fixedShares > 0) {
        await createShield({
          shield_name: 'F7%-Exit', shield_type: 'PRICE',
          shield_percentage: fixedShares.toString(), is_active: true,
          shield_price: +(basePriceFixed * (1 - 7 / 100)).toFixed(4)
        });
      }
      await loadShields({ force: true, skipLoadingState: true });
      triggerShieldRefresh();
      setError('');
      checkAndAutoNavigateToTable();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to apply T3-F7 preset');
    } finally {
      setLoading(false);
    }
  };

  const applyT5T10Preset = async () => {
    if (!tickerInfo) return;
    try {
      setShowAddForm(true);
      setLoading(true);
      const totalShares = customShares !== null ? customShares : (tickerInfo.shares || 0);
      const totalProtectedShares = shields.reduce((total, shield) => total + (parseFloat(shield.shield_shares) || 0), 0);
      const availableShares = Math.max(0, totalShares - totalProtectedShares);
      if (availableShares <= 0) { setError('No available shares to allocate'); return; }
      const currentPrice = tickerInfo.effective_price || tickerInfo.current_price || 0;
      if (!currentPrice || isNaN(currentPrice)) { setError('Missing current price to compute stops'); return; }
      const basePriceFixed = priceCalculationMethod === 'current'
        ? currentPrice
        : (customCostBasis !== null ? customCostBasis : (tickerInfo.cost_basis || currentPrice));
      const half = Math.floor(availableShares / 2);
      const trailingShares = half;
      const fixedShares = availableShares - half;
      if (trailingShares > 0) {
        await createShield({
          shield_name: 'T5%-Trim', shield_type: 'TRAILING_STOP',
          shield_percentage: trailingShares.toString(), is_active: true,
          shield_price: currentPrice, trailing_distance: 5,
          trailing_distance_type: 'PERCENTAGE'
        });
      }
      if (fixedShares > 0) {
        await createShield({
          shield_name: 'F10%-Exit', shield_type: 'PRICE',
          shield_percentage: fixedShares.toString(), is_active: true,
          shield_price: +(basePriceFixed * (1 - 10 / 100)).toFixed(4)
        });
      }
      await loadShields({ force: true, skipLoadingState: true });
      triggerShieldRefresh();
      setError('');
      checkAndAutoNavigateToTable();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to apply T5-T10 preset');
    } finally {
      setLoading(false);
    }
  };

  const applySingleStopLoss = async (percent, providedSize = null) => {
    if (!tickerInfo) return;

    try {
      // Keep user in button selection interface
      setShowAddForm(true);
      setLoading(true);
      const totalShares = customShares !== null ? customShares : (tickerInfo.shares || 0);
      const totalProtectedShares = shields.reduce((total, shield) => total + (parseFloat(shield.shield_shares) || 0), 0);
      const availableShares = Math.max(0, totalShares - totalProtectedShares);

      if (availableShares <= 0) {
        setError('No available shares to allocate');
        return;
      }

      // Use provided size if available, otherwise fall back to state (default to 100%)
      const sizePercentage = providedSize !== null ? providedSize : (singleStopSizes[percent] || 100);
      // Calculate shares based on TOTAL shares, not available shares
      const sharesToProtect = Math.round(totalShares * (sizePercentage / 100));

      // Ensure we don't exceed available shares
      const actualSharesToProtect = Math.min(sharesToProtect, availableShares);

      if (actualSharesToProtect <= 0) {
        setError('Insufficient shares for selected size');
        return;
      }

      // Check if we're creating trailing stops
      const isTrailingStops = priceCalculationMethod === 'trailing';

      // For trailing stops, we use effective price; for fixed stops, use selected method
      const basePrice = isTrailingStops || priceCalculationMethod === 'current'
        ? (tickerInfo.effective_price || tickerInfo.current_price || 0)
        : (customCostBasis !== null ? customCostBasis : (tickerInfo.cost_basis || 0));

      if (!basePrice || isNaN(basePrice)) {
        setError(`Missing ${priceCalculationMethod === 'current' ? 'current' : isTrailingStops ? 'current' : 'purchase'} price to compute stops`);
        return;
      }

      let shieldData;

      if (isTrailingStops) {
        // Create trailing stop
        shieldData = {
          shield_name: `Trailing -${percent}%`,
          shield_type: 'TRAILING_STOP',
          shield_percentage: actualSharesToProtect.toString(),
          is_active: true,
          shield_price: basePrice, // Initial price set to current price
          trailing_distance: percent,
          trailing_distance_type: 'PERCENTAGE'
        };
      } else {
        // Create fixed stop
        const stopPrice = +(basePrice * (1 - percent / 100)).toFixed(4);
        shieldData = {
        shield_name: `Stop -${percent}%`,
        shield_type: 'PRICE',
        shield_percentage: actualSharesToProtect.toString(),
        is_active: true,
        shield_price: stopPrice
      };
      }
      
      await createShield(shieldData);
      await loadShields({ force: true, skipLoadingState: true });
      triggerShieldRefresh();
      setError('');

      // Check if all shares are protected and auto-navigate to table if so
      checkAndAutoNavigateToTable();
    } catch (err) {
      setError(err.response?.data?.error || `Failed to apply ${percent}% stop loss`);
    } finally {
      setLoading(false);
    }
  };

  const applyMediumPlayPreset = async (providedSize = null) => {
    if (!tickerInfo) return;

    try {
      setShowAddForm(true);
      setLoading(true);

      const totalShares = customShares !== null ? customShares : (tickerInfo.shares || 0);
      const totalProtectedShares = shields.reduce((total, shield) => total + (parseFloat(shield.shield_shares) || 0), 0);
      const availableShares = Math.max(0, totalShares - totalProtectedShares);

      if (availableShares <= 0) {
        setError('No available shares to allocate');
        return;
      }

      const sizePercentage = providedSize !== null ? providedSize : (trailingStopSizes['medium'] || 100);
      const sharesToProtect = Math.round(totalShares * (sizePercentage / 100));
      const actualSharesToProtect = Math.min(sharesToProtect, availableShares);

      if (actualSharesToProtect <= 0) {
        setError('Insufficient shares for selected size');
        return;
      }

      const basePrice = tickerInfo.effective_price || tickerInfo.current_price || 0;

      if (!basePrice || isNaN(basePrice)) {
        setError('Missing current price to compute trailing stop');
        return;
      }

      const shieldData = {
        shield_name: 'Medium Play -6%',
        shield_type: 'TRAILING_STOP',
        shield_percentage: actualSharesToProtect.toString(),
        is_active: true,
        shield_price: basePrice,
        trailing_distance: 6,
        trailing_distance_type: 'PERCENTAGE'
      };

      await createShield(shieldData);
      await loadShields({ force: true, skipLoadingState: true });
      triggerShieldRefresh();
      setError('');

      checkAndAutoNavigateToTable();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to apply Medium Play preset');
    } finally {
      setLoading(false);
    }
  };

  const applyEntryPlayPreset = async (providedSize = null) => {
    if (!tickerInfo) return;

    try {
      // Keep user in button selection interface
      setShowAddForm(true);
      setLoading(true);

      const totalShares = customShares !== null ? customShares : (tickerInfo.shares || 0);
      const totalProtectedShares = shields.reduce((total, shield) => total + (parseFloat(shield.shield_shares) || 0), 0);
      const availableShares = Math.max(0, totalShares - totalProtectedShares);

      if (availableShares <= 0) {
        setError('No available shares to allocate');
        return;
      }

      // Use provided size if available, otherwise fall back to state (default to 100%)
      const sizePercentage = providedSize !== null ? providedSize : (trailingStopSizes['base'] || 100);
      // Calculate shares based on TOTAL shares, not available shares
      const sharesToProtect = Math.round(totalShares * (sizePercentage / 100));
      // Ensure we don't exceed available shares
      const actualSharesToProtect = Math.min(sharesToProtect, availableShares);

      if (actualSharesToProtect <= 0) {
        setError('Insufficient shares for selected size');
        return;
      }

      // Always use current/effective price for trailing stop
      const basePrice = tickerInfo.effective_price || tickerInfo.current_price || 0;

      if (!basePrice || isNaN(basePrice)) {
        setError('Missing current price to compute trailing stop');
        return;
      }

      const percent = 7;

      // Always create trailing stop
      const shieldData = {
        shield_name: 'Base Play -7%',
        shield_type: 'TRAILING_STOP',
        shield_percentage: actualSharesToProtect.toString(),
        is_active: true,
        shield_price: basePrice,
        trailing_distance: percent,
        trailing_distance_type: 'PERCENTAGE'
      };

      await createShield(shieldData);
      await loadShields({ force: true, skipLoadingState: true });
      triggerShieldRefresh();
      setError('');

      // Check if all shares are protected and auto-navigate to table if so
      checkAndAutoNavigateToTable();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to apply Base Play preset');
    } finally {
      setLoading(false);
    }
  };

  const applyVolatilePlayPreset = async (providedSize = null) => {
    if (!tickerInfo) return;

    try {
      // Keep user in button selection interface
      setShowAddForm(true);
      setLoading(true);

      const totalShares = customShares !== null ? customShares : (tickerInfo.shares || 0);
      const totalProtectedShares = shields.reduce((total, shield) => total + (parseFloat(shield.shield_shares) || 0), 0);
      const availableShares = Math.max(0, totalShares - totalProtectedShares);

      if (availableShares <= 0) {
        setError('No available shares to allocate');
        return;
      }

      // Use provided size if available, otherwise fall back to state (default to 100%)
      const sizePercentage = providedSize !== null ? providedSize : (trailingStopSizes['volatile'] || 100);
      // Calculate shares based on TOTAL shares, not available shares
      const sharesToProtect = Math.round(totalShares * (sizePercentage / 100));
      // Ensure we don't exceed available shares
      const actualSharesToProtect = Math.min(sharesToProtect, availableShares);

      if (actualSharesToProtect <= 0) {
        setError('Insufficient shares for selected size');
        return;
      }

      const basePrice = tickerInfo.effective_price || tickerInfo.current_price || 0;

      if (!basePrice || isNaN(basePrice)) {
        setError('Missing current price to compute trailing stop');
        return;
      }

      const shieldData = {
        shield_name: 'Volatile Play -5%',
        shield_type: 'TRAILING_STOP',
        shield_percentage: actualSharesToProtect.toString(),
        is_active: true,
        shield_price: basePrice,
        trailing_distance: 5,
        trailing_distance_type: 'PERCENTAGE'
      };

      await createShield(shieldData);
      await loadShields({ force: true, skipLoadingState: true });
      triggerShieldRefresh();
      setError('');

      // Check if all shares are protected and auto-navigate to table if so
      checkAndAutoNavigateToTable();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to apply Volatile Play preset');
    } finally {
      setLoading(false);
    }
  };

  const applyStrengthPlayPreset = async (providedSize = null) => {
    if (!tickerInfo) return;

    try {
      // Keep user in button selection interface
      setShowAddForm(true);
      setLoading(true);

      const totalShares = customShares !== null ? customShares : (tickerInfo.shares || 0);
      const totalProtectedShares = shields.reduce((total, shield) => total + (parseFloat(shield.shield_shares) || 0), 0);
      const availableShares = Math.max(0, totalShares - totalProtectedShares);

      if (availableShares <= 0) {
        setError('No available shares to allocate');
        return;
      }

      // Use provided size if available, otherwise fall back to state (default to 100%)
      const sizePercentage = providedSize !== null ? providedSize : (trailingStopSizes['strength'] || 100);
      // Calculate shares based on TOTAL shares, not available shares
      const sharesToProtect = Math.round(totalShares * (sizePercentage / 100));
      // Ensure we don't exceed available shares
      const actualSharesToProtect = Math.min(sharesToProtect, availableShares);

      if (actualSharesToProtect <= 0) {
        setError('Insufficient shares for selected size');
        return;
      }

      const basePrice = tickerInfo.effective_price || tickerInfo.current_price || 0;

      if (!basePrice || isNaN(basePrice)) {
        setError('Missing current price to compute trailing stop');
        return;
      }

      const shieldData = {
        shield_name: 'Strength Play -3%',
        shield_type: 'TRAILING_STOP',
        shield_percentage: actualSharesToProtect.toString(),
        is_active: true,
        shield_price: basePrice,
        trailing_distance: 3,
        trailing_distance_type: 'PERCENTAGE'
      };

      await createShield(shieldData);
      await loadShields({ force: true, skipLoadingState: true });
      triggerShieldRefresh();
      setError('');

      // Check if all shares are protected and auto-navigate to table if so
      checkAndAutoNavigateToTable();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to apply Strength Play preset');
    } finally {
      setLoading(false);
    }
  };

  const applyLongPlayPreset = async (providedSize = null) => {
    if (!tickerInfo) return;

    try {
      // Keep user in button selection interface
      setShowAddForm(true);
      setLoading(true);

      const totalShares = customShares !== null ? customShares : (tickerInfo.shares || 0);
      const totalProtectedShares = shields.reduce((total, shield) => total + (parseFloat(shield.shield_shares) || 0), 0);
      const availableShares = Math.max(0, totalShares - totalProtectedShares);

      if (availableShares <= 0) {
        setError('No available shares to allocate');
        return;
      }

      // Use provided size if available, otherwise fall back to state (default to 100%)
      const sizePercentage = providedSize !== null ? providedSize : (trailingStopSizes['long'] || 100);
      // Calculate shares based on TOTAL shares, not available shares
      const sharesToProtect = Math.round(totalShares * (sizePercentage / 100));
      // Ensure we don't exceed available shares
      const actualSharesToProtect = Math.min(sharesToProtect, availableShares);

      if (actualSharesToProtect <= 0) {
        setError('Insufficient shares for selected size');
        return;
      }

      const basePrice = tickerInfo.effective_price || tickerInfo.current_price || 0;

      if (!basePrice || isNaN(basePrice)) {
        setError('Missing current price to compute trailing stop');
        return;
      }

      const shieldData = {
        shield_name: 'Long Play -9%',
        shield_type: 'TRAILING_STOP',
        shield_percentage: actualSharesToProtect.toString(),
        is_active: true,
        shield_price: basePrice,
        trailing_distance: 9,
        trailing_distance_type: 'PERCENTAGE'
      };

      await createShield(shieldData);
      await loadShields({ force: true, skipLoadingState: true });
      triggerShieldRefresh();
      setError('');

      // Check if all shares are protected and auto-navigate to table if so
      checkAndAutoNavigateToTable();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to apply Long Play preset');
    } finally {
      setLoading(false);
    }
  };

  const applyExtendedPlayPreset = async (providedSize = null) => {
    if (!tickerInfo) return;

    try {
      setShowAddForm(true);
      setLoading(true);

      const totalShares = customShares !== null ? customShares : (tickerInfo.shares || 0);
      const totalProtectedShares = shields.reduce((total, shield) => total + (parseFloat(shield.shield_shares) || 0), 0);
      const availableShares = Math.max(0, totalShares - totalProtectedShares);

      if (availableShares <= 0) {
        setError('No available shares to allocate');
        return;
      }

      const sizePercentage = providedSize !== null ? providedSize : (trailingStopSizes['extended'] || 100);
      const sharesToProtect = Math.round(totalShares * (sizePercentage / 100));
      const actualSharesToProtect = Math.min(sharesToProtect, availableShares);

      if (actualSharesToProtect <= 0) {
        setError('Insufficient shares for selected size');
        return;
      }

      const basePrice = tickerInfo.effective_price || tickerInfo.current_price || 0;

      if (!basePrice || isNaN(basePrice)) {
        setError('Missing current price to compute trailing stop');
        return;
      }

      const shieldData = {
        shield_name: 'Extended Play -10%',
        shield_type: 'TRAILING_STOP',
        shield_percentage: actualSharesToProtect.toString(),
        is_active: true,
        shield_price: basePrice,
        trailing_distance: 10,
        trailing_distance_type: 'PERCENTAGE'
      };

      await createShield(shieldData);
      await loadShields({ force: true, skipLoadingState: true });
      triggerShieldRefresh();
      setError('');

      checkAndAutoNavigateToTable();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to apply Extended Play preset');
    } finally {
      setLoading(false);
    }
  };

  const applyLongLongPlayPreset = async (providedSize = null) => {
    if (!tickerInfo) return;

    try {
      // Keep user in button selection interface
      setShowAddForm(true);
      setLoading(true);

      const totalShares = customShares !== null ? customShares : (tickerInfo.shares || 0);
      const totalProtectedShares = shields.reduce((total, shield) => total + (parseFloat(shield.shield_shares) || 0), 0);
      const availableShares = Math.max(0, totalShares - totalProtectedShares);

      if (availableShares <= 0) {
        setError('No available shares to allocate');
        return;
      }

      // Use provided size if available, otherwise fall back to state (default to 100%)
      const sizePercentage = providedSize !== null ? providedSize : (trailingStopSizes['llong'] || 100);
      // Calculate shares based on TOTAL shares, not available shares
      const sharesToProtect = Math.round(totalShares * (sizePercentage / 100));
      // Ensure we don't exceed available shares
      const actualSharesToProtect = Math.min(sharesToProtect, availableShares);

      if (actualSharesToProtect <= 0) {
        setError('Insufficient shares for selected size');
        return;
      }

      const basePrice = tickerInfo.effective_price || tickerInfo.current_price || 0;

      if (!basePrice || isNaN(basePrice)) {
        setError('Missing current price to compute trailing stop');
        return;
      }

      const shieldData = {
        shield_name: 'LLong Play -12%',
        shield_type: 'TRAILING_STOP',
        shield_percentage: actualSharesToProtect.toString(),
        is_active: true,
        shield_price: basePrice,
        trailing_distance: 12,
        trailing_distance_type: 'PERCENTAGE'
      };

      await createShield(shieldData);
      await loadShields({ force: true, skipLoadingState: true });
      triggerShieldRefresh();
      setError('');

      // Check if all shares are protected and auto-navigate to table if so
      checkAndAutoNavigateToTable();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to apply LLong Play preset');
    } finally {
      setLoading(false);
    }
  };

  const applyLongLongLongPlayPreset = async (providedSize = null) => {
    if (!tickerInfo) return;

    try {
      // Keep user in button selection interface
      setShowAddForm(true);
      setLoading(true);

      const totalShares = customShares !== null ? customShares : (tickerInfo.shares || 0);
      const totalProtectedShares = shields.reduce((total, shield) => total + (parseFloat(shield.shield_shares) || 0), 0);
      const availableShares = Math.max(0, totalShares - totalProtectedShares);

      if (availableShares <= 0) {
        setError('No available shares to allocate');
        return;
      }

      // Use provided size if available, otherwise fall back to state (default to 100%)
      const sizePercentage = providedSize !== null ? providedSize : (trailingStopSizes['lllong'] || 100);
      // Calculate shares based on TOTAL shares, not available shares
      const sharesToProtect = Math.round(totalShares * (sizePercentage / 100));
      // Ensure we don't exceed available shares
      const actualSharesToProtect = Math.min(sharesToProtect, availableShares);

      if (actualSharesToProtect <= 0) {
        setError('Insufficient shares for selected size');
        return;
      }

      const basePrice = tickerInfo.effective_price || tickerInfo.current_price || 0;

      if (!basePrice || isNaN(basePrice)) {
        setError('Missing current price to compute trailing stop');
        return;
      }

      const shieldData = {
        shield_name: 'LLLong Play -15%',
        shield_type: 'TRAILING_STOP',
        shield_percentage: actualSharesToProtect.toString(),
        is_active: true,
        shield_price: basePrice,
        trailing_distance: 15,
        trailing_distance_type: 'PERCENTAGE'
      };

      await createShield(shieldData);
      await loadShields({ force: true, skipLoadingState: true });
      triggerShieldRefresh();
      setError('');

      // Check if all shares are protected and auto-navigate to table if so
      checkAndAutoNavigateToTable();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to apply LLLong Play preset');
    } finally {
      setLoading(false);
    }
  };

  const applyTightPlayPreset = async (providedSize = null) => {
    if (!tickerInfo) return;

    try {
      // Keep user in button selection interface
      setShowAddForm(true);
      setLoading(true);

      const totalShares = customShares !== null ? customShares : (tickerInfo.shares || 0);
      const totalProtectedShares = shields.reduce((total, shield) => total + (parseFloat(shield.shield_shares) || 0), 0);
      const availableShares = Math.max(0, totalShares - totalProtectedShares);

      if (availableShares <= 0) {
        setError('No available shares to allocate');
        return;
      }

      // Use provided size if available, otherwise fall back to state (default to 100%)
      const sizePercentage = providedSize !== null ? providedSize : (trailingStopSizes['tight'] || 100);
      // Calculate shares based on TOTAL shares, not available shares
      const sharesToProtect = Math.round(totalShares * (sizePercentage / 100));
      // Ensure we don't exceed available shares
      const actualSharesToProtect = Math.min(sharesToProtect, availableShares);

      if (actualSharesToProtect <= 0) {
        setError('Insufficient shares for selected size');
        return;
      }

      const basePrice = tickerInfo.effective_price || tickerInfo.current_price || 0;

      if (!basePrice || isNaN(basePrice)) {
        setError('Missing current price to compute trailing stop');
        return;
      }

      const shieldData = {
        shield_name: 'Tight Play -2%',
        shield_type: 'TRAILING_STOP',
        shield_percentage: actualSharesToProtect.toString(),
        is_active: true,
        shield_price: basePrice,
        trailing_distance: 2,
        trailing_distance_type: 'PERCENTAGE'
      };

      await createShield(shieldData);
      await loadShields({ force: true, skipLoadingState: true });
      triggerShieldRefresh();
      setError('');

      // Check if all shares are protected and auto-navigate to table if so
      checkAndAutoNavigateToTable();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to apply Tight Play preset');
    } finally {
      setLoading(false);
    }
  };

  const handleSizeSelection = (sizePercent) => {
    const { type, key } = sizeSelectionModal;

    if (type === 'single') {
      setSingleStopSizes(prev => ({ ...prev, [key]: sizePercent }));
      setSizeSelectionModal({ show: false, type: null, key: null });
      // Pass sizePercent directly to avoid async state update issue
      applySingleStopLoss(key, sizePercent);
    } else if (type === 'trailing') {
      setTrailingStopSizes(prev => ({ ...prev, [key]: sizePercent }));
      setSizeSelectionModal({ show: false, type: null, key: null });
      // Execute the trailing stop preset function with size passed directly
      if (key === 'strength') applyStrengthPlayPreset(sizePercent);
      else if (key === 'medium') applyMediumPlayPreset(sizePercent);
      else if (key === 'base') applyEntryPlayPreset(sizePercent);
      else if (key === 'long') applyLongPlayPreset(sizePercent);
      else if (key === 'extended') applyExtendedPlayPreset(sizePercent);
      else if (key === 'llong') applyLongLongPlayPreset(sizePercent);
      else if (key === 'lllong') applyLongLongLongPlayPreset(sizePercent);
      else if (key === 'volatile') applyVolatilePlayPreset(sizePercent);
      else if (key === 'tight') applyTightPlayPreset(sizePercent);
    }
  };

  const renderSizeSelectionModal = () => (
    <Modal
      show={sizeSelectionModal.show}
      onHide={() => setSizeSelectionModal({ show: false, type: null, key: null })}
      centered
      size="sm"
    >
      <Modal.Header closeButton>
        <Modal.Title>Select Size</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="d-flex gap-2 justify-content-center">
          {[15, 25, 50, 100].map(sizePercent => (
            <Button
              key={sizePercent}
              variant={singleStopSizes[sizeSelectionModal.key] === sizePercent ? 'primary' : 'outline-primary'}
              onClick={() => handleSizeSelection(sizePercent)}
              className="rounded-pill px-3 py-2 fw-bold"
              style={{
                fontSize: '1rem',
                minWidth: '65px',
                boxShadow: singleStopSizes[sizeSelectionModal.key] === sizePercent ? '0 3px 8px rgba(13, 110, 253, 0.3)' : 'none'
              }}
            >
              {sizePercent}%
            </Button>
          ))}
        </div>
      </Modal.Body>
    </Modal>
  );

  // Big circular "+" used by the aggregate Shields modal to send the user to Trade
  // Details, since stops can only be created/edited per buy from that page now.
  const renderAddStopCircleButton = (disabled = false, disabledTitle = 'All shares are already protected') => {
    if (!tradeId) return null;
    if (disabled) {
      return (
        <button
          type="button"
          className="btn btn-success rounded-circle d-inline-flex align-items-center justify-content-center"
          style={{ width: '90px', height: '90px', opacity: 0.5, cursor: 'not-allowed' }}
          disabled
          title={disabledTitle}
        >
          <i className="bi bi-plus" style={{ fontSize: '2.75rem' }}></i>
        </button>
      );
    }
    return (
      <Link
        to={`/trades/${tradeId}`}
        className="btn btn-success rounded-circle d-inline-flex align-items-center justify-content-center"
        style={{ width: '90px', height: '90px' }}
        title="Set up a new stop from Trade Details"
      >
        <i className="bi bi-plus" style={{ fontSize: '2.75rem' }}></i>
      </Link>
    );
  };

  // Same big circular "+" as renderAddStopCircleButton, but opens the in-modal
  // stop-creation form (renderManualShieldForm + renderStopCreationForm) instead
  // of navigating away -- used by the aggregate Shields modal's "Overall Stops"
  // mode, where stops are created directly against the ticker's average cost
  // basis rather than a specific buy lot.
  const renderAddOverallStopButton = (disabled = false, disabledTitle = 'All shares are already protected') => {
    if (disabled) {
      return (
        <button
          type="button"
          className="btn btn-success rounded-circle d-inline-flex align-items-center justify-content-center"
          style={{ width: '90px', height: '90px', opacity: 0.5, cursor: 'not-allowed' }}
          disabled
          title={disabledTitle}
        >
          <i className="bi bi-plus" style={{ fontSize: '2.75rem' }}></i>
        </button>
      );
    }
    return (
      <button
        type="button"
        className="btn btn-success rounded-circle d-inline-flex align-items-center justify-content-center"
        style={{ width: '90px', height: '90px' }}
        onClick={() => setShowAddForm(true)}
        title="Set up a new stop using average cost basis"
      >
        <i className="bi bi-plus" style={{ fontSize: '2.75rem' }}></i>
      </button>
    );
  };

  // The aggregate Shields modal's shields table (all shields on the ticker,
  // across every buy lot, keyed off the ticker's average cost basis). Shared
  // between both stopsMode branches -- only the trailing "+" control beneath
  // it differs (navigate to Trade Details vs. open the in-modal overall-stop
  // creation form).
  // `allowEdit` is only passed true from the 'overall' stopsMode branch: 'individual'
  // mode renders this table unconditionally regardless of showAddForm, so an Edit button
  // there would flip into edit mode with no form visible to show for it.
  const renderAggregateShieldsTable = (allowEdit = false) => (
    <div className="table-responsive flex-grow-1" style={{ overflowY: 'auto' }}>

      <table className="table table-sm" style={{ textAlign: 'right' }}>
        <thead>
          <tr>
            <th style={{ textAlign: 'left' }}>
              Shield Name
            </th>
            <th>Price</th>
            <th>Shares</th>
            <th>Value</th>
            <th>From Cost</th>
            <th>Potential P&L</th>
            <th style={{ textAlign: 'right' }}>Committed</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {shields.map(shield => {
            const shieldShares = shield.shield_shares || tickerInfo?.shares || 0;

            return (
              <tr key={shield.shield_id}>
                <td style={{ textAlign: 'left' }}>
                  <div className="d-flex align-items-center">
                    <div style={{
                      width: '8px',
                      height: '8px',
                      backgroundColor: getShieldColor(shield.percentage_from_cost, shield, tickerInfo?.current_price),
                      borderRadius: '50%',
                      marginRight: '6px'
                    }}></div>
                    <div>
                    <strong>{formatShieldName(shield.shield_name)}</strong>

                    {!shield.is_active && <small className="text-muted d-block">Inactive</small>}
                    {shield.is_active && (tickerInfo?.effective_price || tickerInfo?.current_price) < shield.shield_price && (
                      <small className="text-danger d-block fw-bold">⚠️ Stop Loss Triggered</small>
                    )}
                  </div>
                  </div>
                </td>
                <td>
                  <strong className={shield.shield_price >= (tickerInfo?.cost_basis || 0) ? 'text-success' : 'text-danger'}>
                    {formatCurrency(shield.shield_price)}
                  </strong>
                </td>
                <td>
                  <strong>{shieldShares}</strong>
                </td>
                <td>
                  <strong>{formatCurrency(shieldShares * shield.shield_price)}</strong>
                </td>
                <td>
                  <span className={shield.percentage_from_cost >= 0 ? 'text-success' : 'text-danger'}>
                    {formatPercentage(shield.percentage_from_cost)}
                  </span>
                </td>
                <td>
                  <div className={`fw-bold ${shield.amount_from_cost >= 0 ? 'text-success' : 'text-danger'}`}>
                    {formatCurrency(shield.amount_from_cost)}
                  </div>
                  <small className="text-muted">
                    {formatCurrency(shield.shield_price - (tickerInfo?.cost_basis || 0))} per share
                  </small>
                </td>
                <td>
                  <div className="text-end">
                    <Button
                      variant={shield.is_committed_to_broker ? 'success' : 'warning'}
                      size="sm"
                      className="rounded-pill px-2 py-0"
                      onClick={() => handleShieldCommitToggle(shield)}
                      disabled={loading || rowCommitLoadingId === shield.shield_id}
                      title={shield.is_committed_to_broker ? 'Committed' : 'Not Committed'}
                      aria-label={shield.is_committed_to_broker ? 'Committed' : 'Not Committed'}
                    >
                      {rowCommitLoadingId === shield.shield_id ? (
                        <span className="spinner-border spinner-border-sm" role="status"></span>
                      ) : (
                        <>
                          <i className={`bi ${shield.is_committed_to_broker ? 'bi-check-circle' : 'bi-exclamation-triangle'} me-1`}></i>
                          {shield.is_committed_to_broker ? 'Yes' : 'No'}
                        </>
                      )}
                    </Button>
                  </div>
                </td>
                <td>
                  <div className="d-flex gap-1 justify-content-end">
                    {allowEdit && (
                      <Button
                        variant="outline-primary"
                        size="sm"
                        onClick={() => handleEditShield(shield)}
                        disabled={loading}
                        title="Edit Shield"
                      >
                        <i className="bi bi-pencil"></i>
                      </Button>
                    )}
                    <Button
                      variant="outline-danger"
                      size="sm"
                      onClick={() => handleDeleteShield(shield.shield_id)}
                      disabled={loading}
                      title="Delete Shield"
                    >
                      <i className="bi bi-trash"></i>
                    </Button>
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
        <tfoot>
          <tr style={{ borderTop: '2px solid var(--border-color, #dee2e6)', backgroundColor: 'var(--bg-secondary, #f8f9fa)' }}>
            <td style={{ textAlign: 'left' }}><strong>Total Effect</strong></td>
            <td>
              {(() => {
                const totalProtectedShares = shields.reduce((total, shield) => total + (parseFloat(shield.shield_shares) || 0), 0);
                const weightedAvgPrice = totalProtectedShares > 0 ? shields.reduce((total, shield) => {
                  const shares = parseFloat(shield.shield_shares) || 0;
                  return total + (shield.shield_price * shares);
                }, 0) / totalProtectedShares : 0;
                return (
                  <strong className={weightedAvgPrice >= (tickerInfo?.cost_basis || 0) ? 'text-success' : 'text-danger'}>
                    {formatCurrency(weightedAvgPrice)}
                  </strong>
                );
              })()}
            </td>
            <td>
              {(() => {
                const totalProtectedShares = shields.reduce((total, shield) => total + (parseFloat(shield.shield_shares) || 0), 0);
                return (
                  <strong>{totalProtectedShares}</strong>
                );
              })()}
            </td>
            <td>
              {(() => {
                const totalValue = shields.reduce((total, shield) => {
                  const shares = parseFloat(shield.shield_shares) || 0;
                  return total + (shares * shield.shield_price);
                }, 0);
                return (
                  <strong>{formatCurrency(totalValue)}</strong>
                );
              })()}
            </td>
            <td>
              {(() => {
                const totalPL = shields.reduce((total, shield) => total + (shield.amount_from_cost || 0), 0);
                const costBasisPositionValue = (tickerInfo?.cost_basis || 0) * (tickerInfo?.shares || 0);
                const overallEffectPercentage = costBasisPositionValue > 0 ? (totalPL / costBasisPositionValue) * 100 : 0;
                return (
                  <strong className={overallEffectPercentage >= 0 ? 'text-success' : 'text-danger'}>
                    {formatPercentage(overallEffectPercentage)}
                  </strong>
                );
              })()}
            </td>
            <td>
              {(() => {
                const totalPL = shields.reduce((total, shield) => total + (shield.amount_from_cost || 0), 0);
                return (
                  <div className={`fw-bold ${totalPL >= 0 ? 'text-success' : 'text-danger'}`}>
                    {formatCurrency(totalPL)}
                  </div>
                );
              })()}
            </td>
            <td className="text-end">
              <Button
                variant={shields.every(s => s.is_committed_to_broker) ? 'success' : 'warning'}
                size="sm"
                className="rounded-pill px-2 py-0"
                disabled={loading || shields.length === 0}
                onClick={() => handleGlobalCommitToggle()}
                title={shields.every(s => s.is_committed_to_broker) ? 'All Committed' : 'Not All Committed'}>
                <i className={`bi ${shields.every(s => s.is_committed_to_broker) ? 'bi-check-circle' : 'bi-exclamation-triangle'} me-1`}></i>
                {shields.every(s => s.is_committed_to_broker) ? 'Yes' : 'No'}
              </Button>
            </td>
          <td className="text-end">
            <Button
              variant="outline-danger"
              size="sm"
              disabled={loading || shields.length === 0}
              onClick={handleDeleteAllShields}
              title="Delete all stop losses">
              <i className="bi bi-trash"></i>
            </Button>
          </td>
          </tr>
        </tfoot>
      </table>
    </div>
  );

  const renderSingleStopButton = (percent, variant, size, customStyle = {}) => {
    const selectedSize = singleStopSizes[percent] || 100;
    const isLarge = size === 'lg';

    // Mirror the basePrice selection in applySingleStop so the price shown here
    // always matches the price that would actually be used if this button is clicked.
    const isTrailingStops = priceCalculationMethod === 'trailing';
    const basePrice = isTrailingStops || priceCalculationMethod === 'current'
      ? (tickerInfo?.effective_price || tickerInfo?.current_price || 0)
      : (customCostBasis !== null ? customCostBasis : (tickerInfo?.cost_basis || 0));
    const effectivePrice = basePrice ? basePrice * (1 - percent / 100) : null;

    return (
      <Button
        key={percent}
        variant={variant}
        size={size}
        disabled={loading}
        onClick={() => setSizeSelectionModal({ show: true, type: 'single', key: percent })}
        className={`${isLarge ? 'rounded-pill px-4 py-2 fw-bold' : 'rounded-pill px-3 py-1'} d-flex flex-column align-items-center`}
        style={customStyle}
        title={`Single ${priceCalculationMethod === 'trailing' ? 'trailing stop with ' + percent + '% distance' : `stop loss at ${percent}% below ${priceCalculationMethod === 'current' ? 'current' : 'purchase'} price`} (Currently: ${selectedSize}% of shares)`}
      >
        <span className="d-block">{percent}%</span>
        {effectivePrice !== null && (
          <small
            className="fw-normal d-block"
            style={{ fontSize: isLarge ? '0.75rem' : '0.65rem', lineHeight: 1.1, opacity: 0.9 }}
          >
            {formatCurrency(effectivePrice)}
          </small>
        )}
      </Button>
    );
  };

  // Shared renderer for the 3 combo buttons (T2%-F5%, T3%-F7%, T5%-F10%),
  // each of which splits available shares 50/50 into a trailing-stop "trim"
  // leg and a fixed-price "exit" leg. Mirrors the basePrice selection in
  // applyT2T5Preset/applyT3T7Preset/applyT5T10Preset so the prices shown here
  // always match what those handlers would actually create.
  const renderComboPresetButton = (trailPercent, fixedPercent, onClick, label) => {
    const currentPrice = tickerInfo?.effective_price || tickerInfo?.current_price || 0;
    const basePriceFixed = priceCalculationMethod === 'current'
      ? currentPrice
      : (customCostBasis !== null ? customCostBasis : (tickerInfo?.cost_basis || currentPrice));
    const trimPrice = currentPrice ? currentPrice * (1 - trailPercent / 100) : null;
    const exitPrice = basePriceFixed ? basePriceFixed * (1 - fixedPercent / 100) : null;

    return (
      <Button
        variant="success"
        disabled={loading}
        onClick={onClick}
        className="rounded-pill fw-bold d-flex flex-column align-items-center"
        style={{
          minWidth: '210px',
          maxWidth: '330px',
          padding: '0.625rem 1.25rem',
          fontSize: '1.25rem',
          boxShadow: '0 3px 8px rgba(25, 135, 84, 0.3)',
          border: '2px solid #198754'
        }}
        title="Creates 2 trailing stops at once (50/50 split)"
      >
        <span className="d-block fw-bold" style={{ fontSize: '1.3rem' }}>{label}</span>
        <small className="text-white" style={{ fontSize: '0.85rem', marginTop: '-2px' }}>
          {formatPercentage(getPresetPreview([trailPercent, fixedPercent], [1, 1]).effectPct)}
        </small>
        {(trimPrice !== null || exitPrice !== null) && (
          <small className="fw-normal d-block" style={{ fontSize: '0.75rem', marginTop: '-1px', opacity: 0.9 }}>
            {trimPrice !== null && `Trim ${formatCurrency(trimPrice)}`}
            {trimPrice !== null && exitPrice !== null && ' · '}
            {exitPrice !== null && `Exit ${formatCurrency(exitPrice)}`}
          </small>
        )}
      </Button>
    );
  };

  const renderPlayPresetButtons = () => {
    const baseButtonClass = 'rounded-pill fw-bold text-center flex-grow-1 flex-sm-grow-0';
    const baseButtonStyle = {
      minWidth: '175px',
      maxWidth: '275px',
      padding: '0.625rem 1.25rem',
      fontSize: '1.25rem'
    };

    const subLabelStyle = {
      fontSize: '1.1rem',
      lineHeight: 1.1
    };

    // Play presets are always single trailing stops off the current/effective
    // price (see applyMediumPlayPreset et al.), regardless of the Entry
    // Strategies price-method toggle -- so a single basePrice covers all of them.
    const basePrice = tickerInfo?.effective_price || tickerInfo?.current_price || 0;

    const renderPreset = (key, label, percent, variant, extraStyle = {}) => {
      const selectedSize = trailingStopSizes[key] || 100;
      const effectivePrice = basePrice ? basePrice * (1 - percent / 100) : null;

      return (
        <Button
          key={key}
          variant={variant}
          disabled={loading}
          onClick={() => setSizeSelectionModal({ show: true, type: 'trailing', key })}
          className={baseButtonClass}
          style={{ ...baseButtonStyle, ...extraStyle }}
          title={`Single trailing stop with ${percent}% distance (Currently: ${selectedSize}% of shares)`}
        >
          <span className="d-block">{label}</span>
          <small className="fw-normal d-block" style={subLabelStyle}>
            {percent}% Trailing stop
          </small>
          {effectivePrice !== null && (
            <small className="fw-normal d-block" style={{ fontSize: '0.9rem', lineHeight: 1.1, opacity: 0.9 }}>
              {formatCurrency(effectivePrice)}
            </small>
          )}
        </Button>
      );
    };

    return (
      <div className="mb-3" style={{ marginTop: '0.5rem' }}>
        <div className="d-flex flex-wrap gap-2 justify-content-center mb-2">
          {renderPreset('medium', 'Medium Play', 6, 'info', { boxShadow: '0 3px 8px rgba(13, 110, 253, 0.2)' })}
          {renderPreset('base', 'Base Play', 7, 'info', { boxShadow: '0 3px 8px rgba(13, 110, 253, 0.2)' })}
          {renderPreset('long', 'Long Play', 9, 'info', { boxShadow: '0 3px 8px rgba(13, 110, 253, 0.2)' })}
          {renderPreset('extended', 'Extended Play', 10, 'info', { boxShadow: '0 3px 8px rgba(13, 110, 253, 0.2)' })}
          {renderPreset('llong', 'LLong Play', 12, 'info', { boxShadow: '0 3px 8px rgba(13, 110, 253, 0.2)' })}
          {renderPreset('lllong', 'LLLong Play', 15, 'info', { boxShadow: '0 3px 8px rgba(13, 110, 253, 0.2)' })}
        </div>
        <div className="d-flex flex-wrap gap-2 justify-content-center">
          {renderPreset('volatile', 'Volatile Play', 5, 'warning', { boxShadow: '0 3px 8px rgba(255, 193, 7, 0.25)', color: '#000' })}
          {renderPreset('strength', 'Strength Play', 3, 'success', { boxShadow: '0 3px 8px rgba(25, 135, 84, 0.25)' })}
          {renderPreset('tight', 'Tight Play', 2, 'primary', { boxShadow: '0 3px 8px rgba(13, 110, 253, 0.25)' })}
        </div>
      </div>
    );
  };

  // Manual custom-shield add/edit form (name/type/price/shares inputs + preview).
  // Shared between the per-lot inline view (Trade Details) and the aggregate
  // "Overall Stops" flow in the Shields modal -- both drive the same
  // showAddForm/newShield/editingShield state, just with createShield tagging
  // the result with a different (or absent) transactionId.
  const renderManualShieldForm = () => {
    if (!showAddForm) return null;

    const shares = customShares !== null ? customShares : (tickerInfo?.shares || 0);

    // Calculate available shares differently for editing vs adding
    let availableShares = 0;
    if (tickerInfo) {
      if (editingShield) {
        // When editing: current shield shares + remaining unprotected shares
        const totalProtectedShares = shields.reduce((total, shield) => {
          return total + (parseFloat(shield.shield_shares) || 0);
        }, 0);
        const remainingShares = Math.max(0, shares - totalProtectedShares);
        availableShares = (parseFloat(editingShield.shield_shares) || 0) + remainingShares;
      } else {
        // When adding: just remaining unprotected shares
        const totalProtectedShares = shields.reduce((total, shield) => {
          return total + (parseFloat(shield.shield_shares) || 0);
        }, 0);
        availableShares = Math.max(0, shares - totalProtectedShares);
      }
    }

    return (
      <div className="p-3 border rounded mb-3" style={{
        backgroundColor: 'var(--card-bg, white)',
        borderColor: 'var(--border-color, #dee2e6)'
      }}>

        {availableShares === 0 && !editingShield ? (
          <div className="alert alert-info">
            <i className="bi bi-info-circle me-2"></i>
            All shares are already protected by existing shields. Remove or modify existing shields to add new ones.
          </div>
        ) : (
          <>


            <div className="row">
              <div className="col-md-4">
                <Form.Group className="mb-2">
                  <Form.Label>Name</Form.Label>
                  <Form.Control
                    type="text"
                    value={newShield.shield_name}
                    onChange={(e) => {
                      updateShieldField('shield_name', e.target.value);
                    }}
                    placeholder="Stop Loss"
                  />
                </Form.Group>
              </div>
              <div className="col-md-4">
                <Form.Group className="mb-2">
                  <Form.Label>Type</Form.Label>
                  <Form.Select
                    value={newShield.shield_type}
                    onChange={(e) => {
                      updateShieldField('shield_type', e.target.value);
                    }}
                  >
                    <option value="PRICE">Fixed Stop</option>
                    <option value="TRAILING_STOP">Trailing Stop</option>
                  </Form.Select>
                </Form.Group>
              </div>
              <div className="col-md-4">
                {newShield.shield_type === 'PRICE' ? (
                  <Form.Group className="mb-2">
                    <Form.Label>Stop Price</Form.Label>
                    <Form.Control
                      type="text"
                      inputMode="decimal"
                      value={newShield.shield_price}
                      onChange={(e) => {
                        updateShieldField('shield_price', e.target.value);
                      }}
                      onBlur={(e) => {
                        updateShieldField('shield_price', normalizeStopPriceInput(e.target.value));
                      }}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          updateShieldField('shield_price', normalizeStopPriceInput(e.currentTarget.value));
                          if (editingShield) {
                            handleUpdateShield();
                          }
                        }
                      }}
                      placeholder="0.00"
                    />
                  </Form.Group>
                ) : (
                  <Form.Group className="mb-2">
                    <Form.Label>Trailing Distance</Form.Label>
                    <div className="d-flex gap-1">
                      <Form.Control
                        type="number"
                        step="0.01"
                        min="0.01"
                        value={newShield.trailing_distance}
                        onChange={(e) => {
                          updateShieldField('trailing_distance', e.target.value);
                        }}
                        placeholder="0.00"
                      />
                      <Form.Select
                        value={newShield.trailing_distance_type}
                        onChange={(e) => {
                          updateShieldField('trailing_distance_type', e.target.value);
                        }}
                        style={{ width: 'auto', minWidth: '60px' }}
                      >
                        <option value="PERCENTAGE">%</option>
                        <option value="DOLLAR">$</option>
                      </Form.Select>
                    </div>
                  </Form.Group>
                )}
              </div>
            </div>

            <div className="row">
              <div className="col-md-4">
                <Form.Group className="mb-2">
                  <Form.Label>
                    Shares
                    <small className="text-muted"> (max {availableShares})</small>
                  </Form.Label>
                  <Form.Control
                    type="number"
                    step="0.01"
                    min="0.01"
                    max={availableShares}
                    value={newShield.shield_shares}
                    onChange={(e) => {
                      const value = parseFloat(e.target.value) || 0;
                      if (value > availableShares) {
                        setError(`Cannot protect more than ${availableShares} available shares`);
                        return;
                      }
                      updateShieldField('shield_shares', e.target.value);
                      if (error) setError('');
                    }}
                    placeholder="0.00"
                  />
                  {availableShares > 0 && (
                    <div className="mt-1">
                      <Button
                        variant="outline-secondary"
                        size="sm"
                        className="me-1"
                        onClick={() => updateShieldField('shield_shares', Math.round(availableShares / 5).toString())}
                      >
                        1/5
                      </Button>
                      <Button
                        variant="outline-secondary"
                        size="sm"
                        className="me-1"
                        onClick={() => updateShieldField('shield_shares', Math.round(availableShares / 4).toString())}
                      >
                        1/4
                      </Button>
                      <Button
                        variant="outline-secondary"
                        size="sm"
                        className="me-1"
                        onClick={() => updateShieldField('shield_shares', Math.round(availableShares / 3).toString())}
                      >
                        1/3
                      </Button>
                      <Button
                        variant="outline-secondary"
                        size="sm"
                        className="me-1"
                        onClick={() => updateShieldField('shield_shares', Math.round(availableShares / 2).toString())}
                      >
                        1/2
                      </Button>
                      <Button
                        variant="outline-secondary"
                        size="sm"
                        onClick={() => updateShieldField('shield_shares', Math.round(availableShares).toString())}
                      >
                        All
                      </Button>
                    </div>
                  )}
                </Form.Group>
              </div>
              <div className="col-md-8">
                <div className="d-flex gap-2 align-items-center h-100">
                  <Button
                    variant="success"
                    size="sm"
                    onClick={editingShield ? handleUpdateShield : handleAddShield}
                    disabled={loading || (availableShares === 0 && !editingShield)}
                  >
                    {loading ? (editingShield ? 'Updating...' : 'Adding...') : (editingShield ? 'Update' : 'Add')}
                  </Button>
                  <Button
                    variant="outline-secondary"
                    size="sm"
                    onClick={() => {
                      setShowAddForm(false);
                      setEditingShield(null);
                      setNewShield({
                        shield_name: '',
                        shield_type: 'PRICE',
                        shield_price: '',
                        shield_shares: '',
                        trailing_distance: '',
                        trailing_distance_type: 'PERCENTAGE'
                      });
                      setError('');
                    }}
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            </div>

            {/* Shield Preview */}
            {newShield.shield_price && newShield.shield_shares && (
              <div className="mt-3 p-2 border rounded" style={{
                backgroundColor: 'var(--card-bg, white)',
                borderColor: 'var(--border-color, #dee2e6)'
              }}>
                <small className="text-muted">Shield Preview:</small>
                <div className="row small">
                  <div className="col-3">
                    <strong>Type:</strong> {newShield.shield_type === 'TRAILING_STOP' ? 'Trailing Stop' : 'Fixed Stop'}
                  </div>
                  <div className="col-3">
                    <strong>Distance:</strong>
                    {newShield.shield_type === 'TRAILING_STOP'
                      ? `${newShield.trailing_distance || 0}${newShield.trailing_distance_type === 'PERCENTAGE' ? '%' : '$'}`
                      : formatCurrency(newShield.shield_price)
                    }
                  </div>
                  <div className="col-3">
                    <strong>Shares:</strong> {newShield.shield_shares}
                  </div>
                  <div className="col-3">
                    <strong>Coverage:</strong> {((newShield.shield_shares / tickerInfo.shares) * 100).toFixed(1)}%
                  </div>
                </div>
                {newShield.shield_type === 'TRAILING_STOP' && tickerInfo && (
                  <div className="row small mt-2">
                    <div className="col-12">
                      <strong>Initial Shield Price:</strong>
                      {(() => {
                        const currentPrice = tickerInfo.effective_price || tickerInfo.current_price;
                        const distance = parseFloat(newShield.trailing_distance) || 0;
                        let initialPrice;
                        if (newShield.trailing_distance_type === 'PERCENTAGE') {
                          initialPrice = currentPrice * (1 - distance / 100);
                        } else {
                          initialPrice = currentPrice - distance;
                        }
                        return ` ${formatCurrency(initialPrice)} (${formatCurrency(currentPrice)} - ${distance}${newShield.trailing_distance_type === 'PERCENTAGE' ? '%' : '$'})`;
                      })()}
                    </div>
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </div>
    );
  };

  // Price-basis toggle + preset single-stop/trailing buttons + play presets.
  // Used both per-lot inline (Trade Details) and for the aggregate "Overall
  // Stops" flow in the Shields modal, where it operates on the ticker's
  // average cost basis and total shares since no customCostBasis/customShares
  // are passed in that context.
  const renderStopCreationForm = () => (
    <div className="d-flex flex-column justify-content-start align-items-center" style={{ minHeight: '60vh', paddingTop: '0.5rem' }}>
      {/* Back Button - Only show when there are existing shields */}
      {shields.length > 0 && (
        <div className="w-100 mb-3">
          <Button
            variant="outline-secondary"
            size="sm"
            onClick={() => {
              setShowAddForm(false);
              setEditingShield(null);
              setError('');
            }}
          >
            <i className="bi bi-arrow-left me-2"></i>
            Back to Shields Table
          </Button>
        </div>
      )}

      {/* Entry Strategies Section */}
      <div className="mb-5 w-100">
        <h5 className="text-center mb-3 fw-bold text-primary">Entry Strategies</h5>

        {/* Price Calculation Method Toggle */}
        <div className="d-flex align-items-center mb-3 p-3 border rounded" style={{
          backgroundColor: 'var(--card-bg, white)',
          borderColor: 'var(--border-color, #dee2e6)'
        }}>
          <div className="me-3">
            <small className="text-muted d-block">Price for Calculation:</small>
            <small className="text-muted d-block">Toggle when deploying stop losses</small>
          </div>
          <div className="btn-group" role="group">
            <input
              type="radio"
              className="btn-check"
              name={`${instanceId}-priceCalculationMethod`}
              id={`${instanceId}-purchasePrice`}
              value="purchase"
              checked={priceCalculationMethod === 'purchase'}
              onChange={(e) => setPriceCalculationMethod(e.target.value)}
            />
            <label
              className="btn btn-outline-primary"
              htmlFor={`${instanceId}-purchasePrice`}
              style={{ fontSize: '0.9rem', padding: '0.375rem 0.75rem' }}
            >
              Purchase Price
            </label>

            <input
              type="radio"
              className="btn-check"
              name={`${instanceId}-priceCalculationMethod`}
              id={`${instanceId}-currentPrice`}
              value="current"
              checked={priceCalculationMethod === 'current'}
              onChange={(e) => setPriceCalculationMethod(e.target.value)}
            />
            <label
              className="btn btn-outline-primary"
              htmlFor={`${instanceId}-currentPrice`}
              style={{ fontSize: '0.9rem', padding: '0.375rem 0.75rem' }}
            >
              Current Price
            </label>

            <input
              type="radio"
              className="btn-check"
              name={`${instanceId}-priceCalculationMethod`}
              id={`${instanceId}-trailingStop`}
              value="trailing"
              checked={priceCalculationMethod === 'trailing'}
              onChange={(e) => setPriceCalculationMethod(e.target.value)}
            />
            <label
              className="btn btn-outline-primary"
              htmlFor={`${instanceId}-trailingStop`}
              style={{ fontSize: '0.9rem', padding: '0.375rem 0.75rem' }}
            >
              Trailing Stop
            </label>
          </div>
        </div>

        {/* Single Stop Loss Buttons */}
        <div className="d-flex gap-2 align-items-center justify-content-center flex-wrap">
          {renderSingleStopButton(0, 'outline-secondary', 'sm', { minWidth: '60px', fontSize: '0.9rem', fontWeight: '600' })}
          {renderSingleStopButton(1, 'outline-success', 'sm', { minWidth: '60px', fontSize: '0.9rem', fontWeight: '600' })}
          {renderSingleStopButton(2, 'success', 'lg', { minWidth: '80px', fontSize: '1.05rem', boxShadow: '0 4px 10px rgba(25, 135, 84, 0.25), 0 4px 10px rgba(13, 202, 240, 0.25)', background: 'linear-gradient(90deg, #198754 0%, #198754 50%, #0dcaf0 50%, #0dcaf0 100%)', border: 'none', color: '#fff' })}
          {renderSingleStopButton(3, 'info', 'lg', { minWidth: '80px', fontSize: '1.05rem', boxShadow: '0 4px 10px rgba(13, 110, 253, 0.25)' })}
          {renderSingleStopButton(4, 'info', 'lg', { minWidth: '80px', fontSize: '1.05rem', boxShadow: '0 4px 10px rgba(13, 110, 253, 0.25)' })}
          {renderSingleStopButton(5, 'info', 'lg', { minWidth: '80px', fontSize: '1.05rem', boxShadow: '0 4px 10px rgba(13, 110, 253, 0.25)' })}
        </div>
        <div className="d-flex gap-2 align-items-center justify-content-center flex-wrap mt-2">
          {renderSingleStopButton(6, 'outline-info', 'sm', { minWidth: '60px', fontSize: '0.9rem', fontWeight: '600' })}
          {renderSingleStopButton(7, 'outline-info', 'sm', { minWidth: '60px', fontSize: '0.9rem', fontWeight: '600' })}
          {renderSingleStopButton(8, 'outline-info', 'sm', { minWidth: '60px', fontSize: '0.9rem', fontWeight: '600' })}
          {renderSingleStopButton(9, 'outline-info', 'sm', { minWidth: '60px', fontSize: '0.9rem', fontWeight: '600' })}
          {renderSingleStopButton(10, 'outline-info', 'sm', { minWidth: '60px', fontSize: '0.9rem', fontWeight: '600' })}
          {renderSingleStopButton(11, 'outline-info', 'sm', { minWidth: '60px', fontSize: '0.9rem', fontWeight: '600' })}
          {renderSingleStopButton(12, 'outline-info', 'sm', { minWidth: '60px', fontSize: '0.9rem', fontWeight: '600' })}
        </div>
        <div className="d-flex gap-2 justify-content-center align-items-center mt-3">
          {renderComboPresetButton(2, 5, applyT2T5Preset, 'T2%-F5%')}
          {renderComboPresetButton(3, 7, applyT3T7Preset, 'T3%-F7%')}
          {renderComboPresetButton(5, 10, applyT5T10Preset, 'T5%-F10%')}
        </div>

      </div>

      {/* Profit Taking Strategies Section */}
      <div className="w-100" style={{ marginTop: '0.5rem' }}>
        <h5 className="text-center mb-3 fw-bold text-success">Profit Taking Strategies</h5>
        {renderPlayPresetButtons()}
      </div>
    </div>
  );

  const handleClose = () => {
    setShowModal(false);
    setError('');
    setShowAddForm(false);
    setEditingShield(null);
    setNewShield({
      shield_name: 'Stop Loss',
      shield_price: '',
      shield_type: 'PRICE',
      shield_shares: '',
      trailing_distance: '',
      trailing_distance_type: 'PERCENTAGE'
    });
    triggerShieldRefresh();
  };

  const handleDefaultNewPositionShieldPresetChange = async (event) => {
    const nextPreset = normalizeDefaultNewPositionShieldPreset(event.target.value);
    const previousPreset = normalizeDefaultNewPositionShieldPreset(accountInfo?.default_new_position_shield_preset);

    setDefaultNewPositionShieldPreset(nextPreset);
    setDefaultPresetSaving(true);

    try {
      await updateAccount(accountId, {
        default_new_position_shield_preset: nextPreset
      });
      setAccountInfo((prev) => ({
        ...(prev || {}),
        account_id: accountId,
        default_new_position_shield_preset: nextPreset
      }));
      try {
        window.dispatchEvent(new CustomEvent('accountDefaultNewPositionShieldPresetChanged', {
          detail: {
            accountId,
            defaultNewPositionShieldPreset: nextPreset
          }
        }));
      } catch (_) {
        // no-op
      }
      setError('');
    } catch (err) {
      setDefaultNewPositionShieldPreset(previousPreset);
      setError(err.response?.data?.error || 'Failed to update default new-position shield setting');
    } finally {
      setDefaultPresetSaving(false);
    }
  };

  const handleAddShield = async () => {
    if (newShield.shield_type === 'PRICE' && !newShield.shield_price) {
      setError('Shield price is required');
      return;
    }
    
    if (newShield.shield_type === 'TRAILING_STOP' && !newShield.trailing_distance) {
      setError('Trailing distance is required');
      return;
    }
    
    if (!newShield.shield_shares || newShield.shield_shares <= 0) {
      setError('Shield shares must be greater than 0');
      return;
    }
    
    // Check remaining shares available
    if (tickerInfo) {
      const totalProtectedShares = shields.reduce((total, shield) => {
        return total + (parseFloat(shield.shield_shares) || 0);
      }, 0);
      const shares = customShares !== null ? customShares : tickerInfo.shares;
      const remainingShares = Math.max(0, shares - totalProtectedShares);
      
      if (parseFloat(newShield.shield_shares) > remainingShares) {
        setError(`Cannot protect more than ${remainingShares} remaining shares`);
        return;
      }
    }

    try {
      setLoading(true);
      
      // Prepare shield data based on type
      const shieldData = {
        shield_name: newShield.shield_name,
        shield_type: newShield.shield_type,
        shield_percentage: newShield.shield_shares,  // Backend expects 'shield_percentage' field
        is_active: true
      };
      
      if (newShield.shield_type === 'PRICE') {
        shieldData.shield_price = newShield.shield_price;
      } else if (newShield.shield_type === 'TRAILING_STOP') {
        shieldData.trailing_distance = newShield.trailing_distance;
        shieldData.trailing_distance_type = newShield.trailing_distance_type;
        // For trailing stops, we'll let the backend calculate the initial shield price
        shieldData.shield_price = tickerInfo ? (tickerInfo.effective_price || tickerInfo.current_price) : 0;
      }

      await createShield(shieldData);
      await loadShields({ force: true, skipLoadingState: true });
      // Keep form open with the just-added shield values, in case there are still unprotected
      // shares left to add another stop for -- but if that just filled the last of them,
      // checkAndAutoNavigateToTable below sends the user back to the shields table instead of
      // leaving them staring at an add form with nothing left to protect.
      setNewShield({
        shield_name: newShield.shield_name,
        shield_price: newShield.shield_price,
        shield_type: newShield.shield_type,
        shield_shares: newShield.shield_shares,
        trailing_distance: newShield.trailing_distance,
        trailing_distance_type: newShield.trailing_distance_type
      });
      setError('');
      triggerShieldRefresh();
      checkAndAutoNavigateToTable();

      // loadShields already calls onArmorUpdate with calculated risk values

    } catch (err) {
      setError(err.response?.data?.error || 'Failed to add shield');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteShield = async (shieldId) => {
    try {
      setLoading(true);

      // Delete the shield
      await deleteTickerShield(tickerId, accountId, shieldId);

      // Reload shields to reflect the changes
      await loadShields({ force: true });

      setError('');
      triggerShieldRefresh();

    } catch (err) {
      console.error('Error in handleDeleteShield:', err);
      setError(err.response?.data?.error || 'Failed to delete shield');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteAllShields = async () => {
    if (!shields || shields.length === 0) return;
    try {
      setLoading(true);
      // One request deleting every shield for this ticker/account in a
      // single commit server-side, instead of one DELETE per shield.
      await deleteAllTickerShields(tickerId, accountId);
      await loadShields({ force: true, skipLoadingState: true });
      setError('');
      triggerShieldRefresh();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to delete all shields');
    } finally {
      setLoading(false);
    }
  };

  const handleEditShield = (shield) => {
    setEditingShield(shield);
    setNewShield({
      shield_name: shield.shield_name,
      shield_price: normalizeStopPriceInput(shield.shield_price),
      shield_type: shield.shield_type,
      shield_shares: shield.shield_shares.toString(),
      trailing_distance: shield.trailing_distance ? shield.trailing_distance.toString() : '',
      trailing_distance_type: shield.trailing_distance_type || 'PERCENTAGE'
    });
    setShowAddForm(true);
  };

  const handleUpdateShield = async () => {
    if (newShield.shield_type === 'PRICE' && !newShield.shield_price) {
      setError('Shield price is required');
      return;
    }
    
    if (newShield.shield_type === 'TRAILING_STOP' && !newShield.trailing_distance) {
      setError('Trailing distance is required');
      return;
    }
    
    if (!newShield.shield_shares || newShield.shield_shares <= 0) {
      setError('Shield shares must be greater than 0');
      return;
    }

    // Check available shares for editing (current shield shares + remaining unprotected shares)
    if (tickerInfo && editingShield) {
      const totalProtectedShares = shields.reduce((total, shield) => {
        return total + (parseFloat(shield.shield_shares) || 0);
      }, 0);
      const remainingShares = Math.max(0, tickerInfo.shares - totalProtectedShares);
      const availableShares = (parseFloat(editingShield.shield_shares) || 0) + remainingShares;
      
      if (parseFloat(newShield.shield_shares) > availableShares) {
        setError(`Cannot protect more than ${availableShares} available shares`);
        return;
      }
    }

    try {
      setLoading(true);
      
      // Prepare shield data based on type
      const shieldData = {
        shield_name: newShield.shield_name,
        shield_type: newShield.shield_type,
        shield_percentage: newShield.shield_shares,  // Backend expects 'shield_percentage' field
        is_active: true
      };
      
      if (newShield.shield_type === 'PRICE') {
        shieldData.shield_price = newShield.shield_price;
      } else if (newShield.shield_type === 'TRAILING_STOP') {
        shieldData.trailing_distance = newShield.trailing_distance;
        shieldData.trailing_distance_type = newShield.trailing_distance_type;
        // For trailing stops, we'll let the backend calculate the initial shield price
        shieldData.shield_price = tickerInfo ? (tickerInfo.effective_price || tickerInfo.current_price) : 0;
      }
      
      // If stop parameters changed, automatically reset commitment to No
      if (editingShield) {
        let stopChanged = false;
        const prevType = editingShield.shield_type;
        const nextType = newShield.shield_type;
        if (prevType !== nextType) {
          stopChanged = true;
        } else if (nextType === 'PRICE') {
          const prevPrice = parseFloat(editingShield.shield_price);
          const nextPrice = parseFloat(newShield.shield_price);
          if (!isNaN(prevPrice) && !isNaN(nextPrice)) {
            stopChanged = prevPrice !== nextPrice;
          }
        } else if (nextType === 'TRAILING_STOP') {
          const prevDist = parseFloat(editingShield.trailing_distance || '');
          const nextDist = parseFloat(newShield.trailing_distance || '');
          const prevDistType = editingShield.trailing_distance_type || 'PERCENTAGE';
          const nextDistType = newShield.trailing_distance_type || 'PERCENTAGE';
          stopChanged = (prevDist !== nextDist) || (prevDistType !== nextDistType);
        }
        if (stopChanged) {
          shieldData.is_committed_to_broker = false;
        }
      }

      armorDebugLog('[ArmorEdit] Updating shield:', {
        tickerId,
        accountId,
        shieldId: editingShield.shield_id,
        shieldData
      });
      
      await updateTickerShield(tickerId, accountId, editingShield.shield_id, shieldData);
      await loadShields({ force: true, skipLoadingState: true });
      // Close the editing form after successful update
      setShowAddForm(false);
      setEditingShield(null);
      setNewShield({
        shield_name: 'Stop Loss',
        shield_price: '',
        shield_type: 'PRICE',
        shield_shares: '',
        trailing_distance: '',
        trailing_distance_type: 'PERCENTAGE'
      });
      setError('');
      triggerShieldRefresh();
      
      // loadShields already calls onArmorUpdate with calculated risk values
      
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update shield');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount || 0);
  };

  const formatPercentage = (percent) => {
    return `${(percent || 0).toFixed(2)}%`;
  };

  // Helper function to format timestamp in local time
  const formatLocalTimestamp = (timestamp) => {
    if (!timestamp) return '';
    
    try {
      // Add 'Z' if it's missing to ensure proper UTC parsing
      const utcString = timestamp.endsWith('Z') ? timestamp : timestamp + 'Z';
      const date = new Date(utcString);
      
      // Check if date is valid
      if (isNaN(date.getTime())) return 'Invalid date';
      
      // Format as local date and time
      return date.toLocaleString('en-US', {
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: true
      });
    } catch (error) {
      console.error('Error formatting timestamp:', error);
      return 'Invalid date';
    }
  };

  // Helper function to get relative time (e.g., "2 minutes ago")
  const getRelativeTime = (timestamp) => {
    if (!timestamp) return '';

    try {
      // Parse the timestamp - handle both UTC (with Z) and local time
      let date;
      if (timestamp.endsWith('Z')) {
        // UTC timestamp, convert to local time
        date = new Date(timestamp);
      } else {
        // Local timestamp, parse directly
        date = new Date(timestamp);
      }
      
      const now = new Date();
      
      // Calculate relative time from the actual timestamp
      const diffMs = now - date;
      const diffMins = Math.floor(diffMs / (1000 * 60));
      const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
      const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
      
      if (diffMins < 1) return 'Just now';
      if (diffMins < 60) return `${diffMins} minute${diffMins !== 1 ? 's' : ''} ago`;
      if (diffHours < 24) return `${diffHours} hour${diffHours !== 1 ? 's' : ''} ago`;
      if (diffDays < 7) return `${diffDays} day${diffDays !== 1 ? 's' : ''} ago`;
      
      return formatLocalTimestamp(timestamp);
    } catch (error) {
      return formatLocalTimestamp(timestamp);
    }
  };

  const formatHighDateDetails = (dateString) => {
    if (!dateString) {
      return 'High date unavailable';
    }

    try {
      const normalized = dateString.includes('T') ? dateString : `${dateString}T00:00:00`;
      const date = new Date(normalized);

      if (Number.isNaN(date.getTime())) {
        return 'High date unavailable';
      }

      const formattedDate = date.toLocaleDateString(undefined, {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
      });

      const now = new Date();
      const diffTime = now.getTime() - date.getTime();

      if (!Number.isFinite(diffTime) || diffTime < 0) {
        return `Recorded on ${formattedDate}`;
      }

      const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));

      if (diffDays === 0) {
        return `Recorded on ${formattedDate} • Today`;
      }

      if (diffDays === 1) {
        return `Recorded on ${formattedDate} • 1 day ago`;
      }

      return `Recorded on ${formattedDate} • ${diffDays} days ago`;
    } catch (error) {
      console.error('Error formatting high date details:', error);
      return 'High date unavailable';
    }
  };

  const renderPeakSummaryLabel = () => (
    <span className="text-success me-3 d-inline-flex align-items-center">
      <i className="bi bi-graph-up me-2"></i>
      Peak Summary:
      <OverlayTrigger
        placement="top"
        delay={{ show: 200, hide: 100 }}
        overlay={
          <Tooltip id="peak-summary-tooltip">
            {'Peak value and the "New high today" badge persist for the rest of the trading day even if prices dip, and reset when the next trading day begins.'}
          </Tooltip>
        }
        trigger={['hover', 'focus']}
      >
        <span
          className="ms-2 text-muted d-inline-flex align-items-center"
          role="button"
          tabIndex={0}
          aria-label="Peak summary timing details"
          style={{ cursor: 'pointer' }}
        >
          <i className="bi bi-info-circle"></i>
        </span>
      </OverlayTrigger>
    </span>
  );

  const renderPricePeakSummary = () => {
    const fixedStops = shields.filter(shield => shield.shield_type === 'PRICE');

    if (fixedStops.length === 0) {
      return (
        <span className="text-muted me-3">
          <i className="bi bi-clock me-1"></i>
          Tracking peak value... (no peak reached yet)
        </span>
      );
    }

    const peakPrice = tickerInfo?.high_since_purchase_price;
    const peakDate = tickerInfo?.high_since_purchase_date;
    const isNewHighToday = tickerInfo?.is_new_high_today;

    if (peakPrice) {
      const highDetails = formatHighDateDetails(peakDate);

      return (
        <>
          <span className="me-3">
            Peak ${parseFloat(peakPrice).toFixed(2)}
          </span>
          <span className="text-muted d-flex align-items-center flex-wrap">
            {highDetails}
            {isNewHighToday && (
              <span className="ms-2 text-warning d-flex align-items-center">
                <i className="bi bi-star-fill me-1"></i>
                New high today
              </span>
            )}
          </span>
        </>
      );
    }

    const firstStop = fixedStops[0];
    if (firstStop.highest_price_seen) {
      return (
        <>
          <span className="me-3">
            Peak ${parseFloat(firstStop.highest_price_seen).toFixed(2)}
          </span>
          <span className="text-muted">
            {getRelativeTime(firstStop.peak_reached_at || firstStop.updated_at)}
          </span>
        </>
      );
    }

    return (
      <span className="text-muted me-3">
        <i className="bi bi-clock me-1"></i>
        Tracking peak value... (no peak reached yet)
      </span>
    );
  };

  // Simple preset preview: equal weighting across provided percentages
  // Example: [1,2,3] => (1 + 2 + 3) / 3 = 2.00%
  const getPresetPreview = (percents, parts) => {
    if (!percents || percents.length === 0) return { effectPct: 0, cover: 0 };

    // If parts are provided, calculate weighted average
    if (parts && parts.length === percents.length) {
      const totalParts = parts.reduce((a, b) => a + b, 0);
      const weightedSum = percents.reduce((sum, pct, i) => sum + (pct * parts[i]), 0);
      const weightedAvg = weightedSum / totalParts;
      return { effectPct: weightedAvg, cover: 0 };
    }

    // Otherwise, calculate simple average
    const avg = percents.reduce((a, b) => a + b, 0) / percents.length;
    return { effectPct: avg, cover: 0 };
  };

  const handleShieldCommitToggle = async (shield) => {
    if (!tickerId || !accountId || !shield) return;
    try {
      setRowCommitLoadingId(shield.shield_id);
      const newStatus = !shield.is_committed_to_broker;
      await updateTickerShield(tickerId, accountId, shield.shield_id, { is_committed_to_broker: newStatus });
      await loadShields({ force: true, skipLoadingState: true });
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update commitment status');
    } finally {
      setRowCommitLoadingId(null);
    }
  };

  const handleGlobalCommitToggle = async () => {
    if (!tickerId || !accountId || !shields || shields.length === 0) return;
    try {
      setLoading(true);
      const allCommitted = shields.every(s => s.is_committed_to_broker);
      const newStatus = !allCommitted;
      // One request updating every shield for this ticker/account in a
      // single commit server-side, instead of one PUT per shield.
      await commitShieldsToBroker(tickerId, accountId, newStatus);
      await loadShields({ force: true, skipLoadingState: true });
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to toggle commit for all shields');
    } finally {
      setLoading(false);
    }
  };

  const getShieldColor = (percentageFromCost, shield = null, currentPrice = null) => {
    // Check if shield is triggered (effective price below stop loss)
    if (shield && currentPrice && shield.is_active && currentPrice < shield.shield_price) {
      return 'var(--bs-purple, #6f42c1)'; // Purple for triggered
    }
    
    // Use theme-aware colors that work in both light and dark modes
    if (percentageFromCost >= -5) return 'var(--bs-success, #198754)'; // Green for small losses
    if (percentageFromCost >= -10) return 'var(--bs-warning, #fd7e14)'; // Orange for medium losses
    if (percentageFromCost >= -15) return 'var(--bs-danger, #dc3545)'; // Red for large losses
    return 'var(--bs-info, #0dcaf0)'; // Blue for very large losses
  };

  // Render shield visualization with left-right layout
  const renderShieldVisualization = () => {
    if (!tickerInfo) return null;

    // Use custom values if provided, otherwise use tickerInfo values
    const current_price = tickerInfo.effective_price || tickerInfo.current_price;
    const cost_basis = customCostBasis !== null ? customCostBasis : tickerInfo.cost_basis;
    const shares = customShares !== null ? customShares : tickerInfo.shares;
    const isClosedTrade = Boolean(isClosed && soldPrice !== null && !isNaN(soldPrice));

    const getShieldSortValue = (shield) => {
      const timestamp = shield.hit_timestamp || shield.updated_at || shield.created_at;
      const dateValue = timestamp ? new Date(timestamp).getTime() : NaN;
      if (Number.isFinite(dateValue)) return dateValue;
      if (shield.shield_id != null) return Number(shield.shield_id);
      return 0;
    };

    const displayShields = isClosedTrade
      ? (() => {
          if (!shields || shields.length === 0) return [];
          const sortedByRecency = [...shields].sort(
            (a, b) => getShieldSortValue(b) - getShieldSortValue(a)
          );
          return sortedByRecency.slice(0, 1);
        })()
      : shields;
    
    const sortedShields = [...displayShields].sort((a, b) => b.shield_price - a.shield_price);

    // Create price levels for visualization
    const priceLevels = [
      { 
        label: isClosedTrade ? 'Sold Price' : 'Current Price', 
        price: isClosedTrade ? soldPrice : current_price, 
        type: isClosedTrade ? 'sold' : 'current',
        color: 'var(--bs-primary, #0d6efd)',
        icon: '📈'
      },
      { 
        label: 'Purchase Price', 
        price: cost_basis, 
        type: 'purchase',
        color: 'var(--text-secondary, #6c757d)',
        icon: '🎯'
      },
      ...sortedShields.map(shield => {
        // For closed trades with trailing stops, use hit price if available
        let displayPrice = shield.shield_price;
        if (isClosed && shield.shield_type === 'TRAILING_STOP' && hitPrices && hitPrices[shield.shield_id]) {
          displayPrice = parseFloat(hitPrices[shield.shield_id].hit_price) || shield.shield_price;
        }
        
        return {
          label: formatShieldName(shield.shield_name),
          price: displayPrice,
          type: 'shield',
          color: (() => {
            // Check if shield is triggered (effective price below stop loss)
            if (shield.is_active && current_price < shield.shield_price) {
              return 'var(--bs-purple, #6f42c1)'; // Purple for triggered
            }
            // Otherwise use profit/loss color
            return shield.amount_from_cost >= 0 ? 'var(--bs-success, #198754)' : 'var(--bs-danger, #dc3545)';
          })(),
          icon: '🛡️',
          shield: shield,
          shieldShares: shield.shield_shares || shares
        };
      })
    ].sort((a, b) => b.price - a.price);

    const maxPrice = Math.max(...priceLevels.map(level => level.price));
    const minPrice = Math.min(...priceLevels.map(level => level.price));
    const priceRange = maxPrice - minPrice || 1; // Avoid division by zero

    return (
      <div className="d-flex flex-column h-100 armor-edit-visualization">
        <div className="d-flex justify-content-center align-items-center mb-3">
          {!isClosedTrade && shields.length > 0 && (() => {
            // Calculate overall effect only for shields relevant to this purchase
            const relevantShields = shields.filter(shield => {
              const totalShieldShares = parseFloat(shield.shield_shares) || 0;
              return Math.min(totalShieldShares, shares) > 0;
            });
            
            const totalShieldPL = relevantShields.reduce((total, shield) => {
              const totalShieldShares = parseFloat(shield.shield_shares) || 0;
              const shieldShares = Math.min(totalShieldShares, shares);
              return total + (shieldShares * (shield.shield_price - cost_basis));
            }, 0);
            
            const costBasisPositionValue = cost_basis * shares;
            const overallEffectPercentage = costBasisPositionValue > 0 ? (totalShieldPL / costBasisPositionValue) * 100 : 0;
            
            return (
              <div className="badge" style={{ 
                backgroundColor: overallEffectPercentage >= 0 ? 'var(--bs-success, #198754)' : 'var(--bs-danger, #dc3545)',
                color: 'white',
                fontSize: '13px',
                padding: '6px 12px'
              }}>
                Overall Effect: {formatPercentage(overallEffectPercentage)} ({formatCurrency(totalShieldPL)})
              </div>
            );
          })()}
        </div>
        <div className="flex-grow-1">
          {/* Simplified Price Level Visualization with Overlay Stats */}
          <div className="armor-edit-visualization-frame" style={{ 
            height: '100%', 
            border: '1px solid var(--border-color, #dee2e6)', 
            borderRadius: '4px',
            padding: '20px',
            background: 'var(--bg-secondary, #f8f9fa)',
            position: 'relative'
          }}>
            {/* Background fill between Purchase and Sold when trade is closed */}
            {isClosedTrade && (() => {
              const soldPosition = ((soldPrice - minPrice) / priceRange) * 80;
              const purchasePosition = ((cost_basis - minPrice) / priceRange) * 80;
              const fillTop = Math.min(soldPosition, purchasePosition) + 10;
              const fillBottom = Math.max(soldPosition, purchasePosition) + 10;
              const fillHeight = Math.abs(fillBottom - fillTop);
              if (fillHeight < 0.5) return null;
              const isGain = soldPrice >= cost_basis;
              return (
                <div
                  style={{
                    position: 'absolute',
                    left: '10px',
                    right: '10px',
                    bottom: `${fillTop}%`,
                    height: `${fillHeight}%`,
                    backgroundColor: isGain
                      ? 'rgba(25, 135, 84, 0.18)'
                      : 'rgba(220, 53, 69, 0.18)',
                    borderLeft: `4px solid ${isGain ? 'var(--bs-success, #198754)' : 'var(--bs-danger, #dc3545)'}`,
                    borderRadius: '3px',
                    zIndex: 2,
                    boxShadow: 'inset 0 0 0 1px rgba(0,0,0,0.03)'
                  }}
                />
              );
            })()}
            {/* Background fills for shield distances */}
            {sortedShields.map((shield, index) => {
              // For closed trades with trailing stops, use hit price if available
              let shieldPrice = shield.shield_price;
              if (isClosed && shield.shield_type === 'TRAILING_STOP' && hitPrices && hitPrices[shield.shield_id]) {
                shieldPrice = parseFloat(hitPrices[shield.shield_id].hit_price) || shield.shield_price;
              }
              
              const isAbovePurchase = shieldPrice >= cost_basis;
              const isTriggered = shield.is_active && current_price < shield.shield_price;
              
              // Calculate positions for the fill area using the same coordinate system as price bars
              const shieldPosition = ((shieldPrice - minPrice) / priceRange) * 80;
              const purchasePosition = ((cost_basis - minPrice) / priceRange) * 80;
              
              // Determine the top and bottom of the fill area
              const fillTop = Math.min(shieldPosition, purchasePosition) + 10;
              const fillBottom = Math.max(shieldPosition, purchasePosition) + 10;
              const fillHeight = Math.abs(fillBottom - fillTop);
              
              // Show fill for any meaningful distance
              if (fillHeight < 0.5) return null;
              
              return (
                <div
                  key={`fill-${shield.shield_id}`}
                  style={{
                    position: 'absolute',
                    left: '10px',
                    right: '10px',
                    bottom: `${fillTop}%`,
                    height: `${fillHeight}%`,
                    backgroundColor: (() => {
                      if (isTriggered) {
                        return 'rgba(111, 66, 193, 0.4)'; // Purple for triggered
                      }
                      return isAbovePurchase 
                        ? 'rgba(25, 135, 84, 0.3)' // Green with more opacity
                        : 'rgba(220, 53, 69, 0.3)'; // Red with more opacity
                    })(),
                    borderLeft: `4px solid ${(() => {
                      if (isTriggered) {
                        return 'var(--bs-purple, #6f42c1)'; // Purple for triggered
                      }
                      return isAbovePurchase ? 'var(--bs-success, #198754)' : 'var(--bs-danger, #dc3545)';
                    })()}`,
                    borderRadius: '3px',
                    zIndex: 1,
                    boxShadow: 'inset 0 0 0 1px rgba(0,0,0,0.05)'
                  }}
                />
              );
            })}

            {/* Price level bars with overlay statistics */}
            {priceLevels.map((level, index) => {
              const positionPercent = ((level.price - minPrice) / priceRange) * 80; // 80% of container height for bars
              
              return (
                <div
                  key={index}
                  style={{
                    position: 'absolute',
                    left: '10px',
                    right: '10px',
                    bottom: `${positionPercent + 10}%`,
                    height: '6px',
                    backgroundColor: level.color,
                    borderRadius: '3px',
                    display: 'flex',
                    alignItems: 'center',
                    boxShadow: '0 2px 4px var(--shadow-color, rgba(0,0,0,0.1))',
                    zIndex: 5
                  }}
                >
                  <div style={{
                    position: 'absolute',
                    left: '-8px',
                    width: '16px',
                    height: '16px',
                    backgroundColor: level.color,
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '8px',
                    border: '2px solid var(--card-bg, white)',
                    boxShadow: '0 2px 4px var(--shadow-color, rgba(0,0,0,0.2))',
                    zIndex: 10
                  }}>
                    {level.icon}
                  </div>
                  
                  {/* Price label */}
                  <div style={{
                    position: 'absolute',
                    right: '0px',
                    top: '-20px',
                    fontSize: '13px',
                    fontWeight: 'bold',
                    color: level.color,
                    background: 'var(--card-bg, white)',
                    padding: '2px 6px',
                    borderRadius: '2px',
                    border: `1px solid ${level.color}`,
                    whiteSpace: 'nowrap',
                    zIndex: 10,
                    textAlign: 'center'
                  }}>
                    {formatCurrency(level.price)}
                  </div>

                  {/* Key statistics overlay - sitting on top of bars */}
                  <div style={{
                    position: 'absolute',
                    top: '-25px',
                    left: '10px',
                    fontSize: '14px',
                    background: 'var(--card-bg, white)',
                    padding: '4px 8px',
                    borderRadius: '4px',
                    border: `1px solid ${level.color}`,
                    boxShadow: '0 2px 6px var(--shadow-color, rgba(0,0,0,0.12))',
                    zIndex: 10,
                    whiteSpace: 'nowrap',
                    minWidth: '140px'
                  }}>
                    {/* Single row layout for all types */}
                    {level.type === 'shield' && level.shield && (
                      <div style={{ color: level.color, fontWeight: 'bold', fontSize: '13px' }}>
                        {level.icon} {level.label} • 
                        <span className={level.shield.amount_from_cost >= 0 ? 'text-success' : 'text-danger'}>
                          {cost_basis > 0 ? formatPercentage(((level.price - cost_basis) / cost_basis) * 100) : 'N/A'}
                        </span>
                      </div>
                    )}
                    
                    {(level.type === 'current' || level.type === 'sold') && (
                      <div style={{ color: level.color, fontWeight: 'bold', fontSize: '13px' }}>
                        {level.icon} {level.label} • 
                        <span className={cost_basis > 0 ? 
                          (level.price >= cost_basis ? 'text-success' : 'text-danger') : 
                          'text-muted'
                        }>
                          {cost_basis > 0 ? 
                            formatPercentage(((level.price - cost_basis) / cost_basis) * 100) : 
                            'N/A'
                          }
                        </span>
                      </div>
                    )}
                    
                    {level.type === 'purchase' && (
                      <div style={{ color: level.color, fontWeight: 'bold', fontSize: '13px' }}>
                        {level.icon} {level.label}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    );
  };

  // Render shield for icon display (simplified version for table)
  const renderShieldIcon = () => {
    const shieldCount = shields.length;
    const iconClass = shieldCount > 0 ? 'bi-shield-fill' : 'bi-shield';
    
    // Calculate protection percentage
    let protectionPercentage = 0;
    let primaryColor = 'var(--bs-primary, #0d6efd)'; // Default blue color
    
    if (shieldCount > 0 && tickerInfo) {
      const totalProtectedShares = shields.reduce((total, shield) => {
        return total + (parseFloat(shield.shield_shares) || 0);
      }, 0);
      protectionPercentage = (totalProtectedShares / tickerInfo.shares) * 100;
      
      // Check if any shields are not committed to broker
      const hasUncommittedShields = shields.some(shield => !shield.is_committed_to_broker);
      
      // Check if any active shields have been triggered (effective price below stop loss)
      const hasTriggeredStopLoss = shields.some(shield => 
        shield.is_active && 
        (tickerInfo.effective_price || tickerInfo.current_price) < shield.shield_price
      );
      
      if (hasUncommittedShields) {
        // White color for uncommitted shields (with outline in light mode for visibility)
        primaryColor = 'white';
      } else if (hasTriggeredStopLoss) {
        // Purple color when stop loss has been triggered
        primaryColor = 'var(--bs-purple, #6f42c1)';
      } else {
        // Set color based on protection level only when committed and not triggered
        if (protectionPercentage >= 100) {
          primaryColor = 'var(--bs-success, #198754)'; // Green for fully protected
        } else if (protectionPercentage >= 50) {
          primaryColor = 'var(--bs-warning, #fd7e14)'; // Yellow/Orange for >50% but <100%
        } else {
          primaryColor = 'var(--bs-danger, #dc3545)'; // Red for <50%
        }
      }
    }
    
    // Build tooltip text that lists shield names only
    let tooltipText = '';
    if (shieldCount > 0) {
      // Sort shields by price (highest to lowest) to preserve previous ordering behaviour
      const sortedShields = [...shields].sort((a, b) => (parseFloat(b.shield_price) || 0) - (parseFloat(a.shield_price) || 0));

      const shieldNames = sortedShields.map((shield) => {
        if (shield.shield_name && shield.shield_name.trim()) {
          return shield.shield_name.trim();
        }

        if (shield.shield_type === 'TRAILING_STOP') {
          const distance = shield.trailing_distance;
          const distanceType = shield.trailing_distance_type;
          if (distance && distanceType) {
            const suffix = distanceType === 'PERCENTAGE' ? '%' : '$';
            return `Trailing Stop -${distance}${suffix}`;
          }
          return 'Trailing Stop';
        }

        if (shield.shield_price) {
          const price = parseFloat(shield.shield_price);
          if (!Number.isNaN(price)) {
            return `Stop Loss $${price.toFixed(2)}`;
          }
        }

        return 'Stop Loss';
      });

      tooltipText = shieldNames.filter(Boolean).join('\n');
    } else {
      tooltipText = 'No shields configured - Click to add';
    }

    if (!tooltipText) {
      tooltipText = 'Shields configured';
    }
    
    return (
      <div 
        style={{ cursor: 'pointer', textAlign: 'center' }}
        onClick={handleShow}
        title={tooltipText}
      >
        <div style={{ position: 'relative', display: 'inline-block' }}>
          <i 
            className={`bi ${iconClass}`} 
            style={{ 
              color: primaryColor, 
              fontSize: '1.525rem',
              // Add outline in light mode when color is white (uncommitted shields)
              ...(primaryColor === 'white' ? {
                textShadow: '0 0 1px #999, 0 0 2px #999, 0 0 3px #999',
                filter: 'drop-shadow(0 0 1px rgba(0,0,0,0.5))'
              } : {})
            }}
          ></i>
          {shieldCount > 0 && (
            <span style={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              backgroundColor: 'var(--bs-danger, #dc3545)',
              color: 'white',
              borderRadius: '8px',
              padding: '1px 3px',
              fontSize: '8px',
              fontWeight: 'bold',
              lineHeight: '1',
              minWidth: '12px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              {shieldCount}
            </span>
          )}
        </div>
      </div>
    );
  };

  // Helper function to generate shield name based on type and parameters
  const generateShieldName = (shieldType, shieldPrice, trailingDistance, trailingDistanceType, shieldId = null) => {
    if (shieldType === 'TRAILING_STOP') {
      if (trailingDistance && trailingDistanceType) {
        const suffix = trailingDistanceType === 'PERCENTAGE' ? '%' : '$';
        // For closed trades with trailing stops, show hit price if available
        if (isClosed && hitPrices && shieldId && hitPrices[shieldId]) {
          const hitPrice = parseFloat(hitPrices[shieldId].hit_price);
          return `Trailing Stop -${trailingDistance}${suffix} (Hit: $${hitPrice.toFixed(2)})`;
        }
        return `Trailing Stop -${trailingDistance}${suffix}`;
      }
      return 'Trailing Stop';
    } else if (shieldType === 'PRICE') {
      if (shieldPrice) {
        return `Stop Loss $${parseFloat(shieldPrice).toFixed(2)}`;
      }
      return 'Stop Loss';
    }
    return 'Stop Loss';
  };

  const normalizeStopPriceInput = (value) => {
    if (value === '' || value === null || value === undefined) {
      return '';
    }
    const numeric = Number(value);
    if (!Number.isFinite(numeric)) {
      return value;
    }
    return numeric.toFixed(2);
  };

  // Helper function to update shield field and automatically update shield name
  const updateShieldField = (field, value) => {
    const updatedShield = { ...newShield, [field]: value };

    // Only auto-generate shield name if:
    // 1. The field being updated is not shield_name itself
    // 2. We're NOT editing an existing shield (editingShield is null)
    if (field !== 'shield_name' && !editingShield) {
      const newName = generateShieldName(
        updatedShield.shield_type,
        updatedShield.shield_price,
        updatedShield.trailing_distance,
        updatedShield.trailing_distance_type
      );
      setNewShield({ ...updatedShield, shield_name: newName });
    } else {
      setNewShield(updatedShield);
    }

    if (error) setError('');
  };

  if (inline) {
    // Use custom values if provided, otherwise use tickerInfo values
    const current_price = tickerInfo?.effective_price || tickerInfo?.current_price || 0;
    const cost_basis = customCostBasis !== null ? customCostBasis : (tickerInfo?.cost_basis || 0);
    const shares = customShares !== null ? customShares : (tickerInfo?.shares || 0);
    const isClosedTrade = Boolean(isClosed && soldPrice !== null && !isNaN(soldPrice));

    // Fully sold lots have nothing left to protect -- skip the shield management UI
    // (summary tiles, price visualization, shields table) for a compact card, but keep
    // rendering closedRightContent (e.g. Post Analysis) since closed trades are always
    // fully sold and would otherwise lose that panel entirely.
    if (isSoldOut) {
      return (
        <div className="p-2">
          <div className="text-center text-muted small">
            <i className="bi bi-check-circle me-1"></i>
            Fully sold - no protection needed.
          </div>
          {isClosedTrade && closedRightContent && (
            <div className="mt-2 d-flex flex-column flex-grow-1">
              <div className="flex-grow-1 d-flex flex-column">
                {closedRightContent}
              </div>
            </div>
          )}
        </div>
      );
    }

    // Render the full shield management UI directly (the contents of <Modal.Body>), not in a modal
    return (
      <>
      <div className="armor-edit-inline-container">
        <style jsx="true">{`
          /* (copy the same dark mode and layout styles as in the Modal) */
          [data-theme="dark"] .form-control {
            background-color: var(--input-bg, #2a2a2a);
            color: var(--input-text, #d0d0d0);
            border-color: var(--border-color, #333333);
          }
          [data-theme="dark"] .form-control:focus {
            background-color: var(--input-bg, #2a2a2a);
            color: var(--input-text, #d0d0d0);
            border-color: #0d6efd;
            box-shadow: 0 0 0 0.2rem rgba(13, 110, 253, 0.25);
          }
          [data-theme="dark"] .table {
            color: var(--text-primary, #e0e0e0);
            --bs-table-bg: var(--card-bg, #1e1e1e);
          }
          [data-theme="dark"] .table th {
            border-color: var(--border-color, #333333);
            background-color: var(--bg-secondary, #1e1e1e);
          }
          [data-theme="dark"] .table td {
            border-color: var(--border-color, #333333);
          }
          [data-theme="dark"] .btn-outline-secondary {
            color: var(--text-primary, #e0e0e0);
            border-color: var(--border-color, #333333);
          }
          [data-theme="dark"] .btn-outline-secondary:hover {
            background-color: var(--hover-bg, #2c2c2c);
            color: var(--text-primary, #e0e0e0);
            border-color: var(--border-color, #333333);
          }
          [data-theme="dark"] .btn-outline-danger {
            color: #ff3b30;
            border-color: #ff3b30;
          }
          [data-theme="dark"] .btn-outline-danger:hover {
            background-color: #ff3b30;
            color: white;
          }
          [data-theme="dark"] .text-muted {
            color: var(--text-secondary, #a0a0a0) !important;
          }
          [data-theme="dark"] .badge.bg-secondary {
            background-color: var(--bg-secondary, #1e1e1e) !important;
            color: var(--text-primary, #e0e0e0) !important;
            border: 1px solid var(--border-color, #333333);
          }
          
          /* Custom column widths for 10% reduction in visual section */
          .armor-edit-inline-container .col-md-3 {
            flex: 0 0 27% !important;
            max-width: 27% !important;
          }
          .armor-edit-inline-container .col-md-9 {
            flex: 0 0 73% !important;
            max-width: 73% !important;
          }
          
          /* Expanded layout - match modal exactly */
          .armor-edit-expanded .col-md-3 {
            flex: 0 0 27% !important;
            max-width: 27% !important;
          }
          .armor-edit-expanded .col-md-9 {
            flex: 0 0 73% !important;
            max-width: 73% !important;
          }

          .armor-edit-inline-container .armor-edit-visualization,
          .armor-edit-inline-container .armor-edit-visualization-frame {
            min-height: 360px;
          }
          

        `}</style>
        <div className="d-flex flex-column h-100 armor-edit-expanded">
          <div className="row flex-grow-1">
            {/* Left Column - Shield Visualization */}
            <div className="col-md-3 d-flex flex-column">
              {renderShieldVisualization()}
            </div>
            {/* Right Column - Stats (hide shields management on closed trades) */}
            <div className="col-md-9 d-flex flex-column">
              {error && <Alert variant="danger">{error}</Alert>}
              
              {loading && (
                <div className="text-center">
                  <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                </div>
              )}

              {/* Combined Portfolio & Shield Statistics */}
              {tickerInfo && (() => {
                
                // Shield calculations - only include shields relevant to this purchase
                const relevantShields = shields.filter(shield => {
                  const totalShieldShares = parseFloat(shield.shield_shares) || 0;
                  return Math.min(totalShieldShares, shares) > 0;
                });
                
                const totalPotentialPL = relevantShields.reduce((total, shield) => {
                  const totalShieldShares = parseFloat(shield.shield_shares) || 0;
                  const shieldShares = Math.min(totalShieldShares, shares);
                  return total + (shieldShares * (shield.shield_price - cost_basis));
                }, 0);
                
                const totalProtectedShares = relevantShields.reduce((total, shield) => {
                  const totalShieldShares = parseFloat(shield.shield_shares) || 0;
                  return total + Math.min(totalShieldShares, shares);
                }, 0);
                
                // Calculate remaining shares for protection
                const remainingShares = Math.max(0, shares - totalProtectedShares);
                
                const coveragePercentage = totalProtectedShares > 0 ? (totalProtectedShares / shares) * 100 : 0;
                // Overall shields effect as percentage of cost basis position value
                const costBasisPositionValue = cost_basis * shares;
                const overallEffectPercentage = costBasisPositionValue > 0 ? (totalPotentialPL / costBasisPositionValue) * 100 : 0;
                
                return (
                  <div className="mb-2">
                    {(() => {
                      const totalCostBasis = cost_basis * shares;
                      const displayUnitPrice = isClosedTrade ? soldPrice : current_price;
                      const displayValue = displayUnitPrice * shares;
                      const totalGain = displayValue - totalCostBasis;
                      const totalGainPct = totalCostBasis > 0 ? (totalGain / totalCostBasis) * 100 : 0;
                      
                      return (
                        <div className="mb-3 text-center">
                          <span className="me-4">
                            <span className="text-muted">Cost Basis: </span>
                            <span className="fw-bold text-secondary">{formatCurrency(totalCostBasis)}</span>
                          </span>
                          <span className="me-4">
                            <span className="text-muted">{isClosedTrade ? 'Sold Value' : 'Current Value'}: </span>
                            <span className="fw-bold text-primary">{formatCurrency(displayValue)}</span>
                          </span>
                          <span>
                            <span className="text-muted">Total Gain: </span>
                            <span className={`fw-bold ${totalGain >= 0 ? 'text-success' : 'text-danger'}`}>
                              {formatCurrency(totalGain)} ({formatPercentage(totalGainPct)})
                            </span>
                          </span>
                        </div>
                      );
                    })()}
                    <div className="row">
                      {/* Row 1 - Basic Portfolio Info */}
                      <div className="col-3 mb-2">
                        <div className="card border-0" style={{ backgroundColor: 'var(--bg-secondary, #f8f9fa)' }}>
                          <div className="card-body text-center p-2">
                            <div className="text-muted" style={{ fontSize: '0.88rem' }}>{isClosedTrade ? 'Sold Price' : 'Current Price'}</div>
                            <div className="fw-bold text-primary mb-0" style={{ fontSize: '1.21rem' }}>
                              {formatCurrency(isClosedTrade ? soldPrice : current_price)}
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className="col-3 mb-2">
                        <div className="card border-0" style={{ backgroundColor: 'var(--bg-secondary, #f8f9fa)' }}>
                          <div className="card-body text-center p-2">
                            <div className="text-muted" style={{ fontSize: '0.88rem' }}>Purchase Price</div>
                            <div className="fw-bold text-secondary mb-0" style={{ fontSize: '1.21rem' }}>
                              {formatCurrency(cost_basis)}
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className="col-3 mb-2">
                        <div className="card border-0" style={{ backgroundColor: 'var(--bg-secondary, #f8f9fa)' }}>
                          <div className="card-body text-center p-2">
                            <div className="text-muted" style={{ fontSize: '0.88rem' }}>Total Shares</div>
                            <div className="fw-bold mb-0" style={{ fontSize: '1.21rem' }}>
                              {shares}
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className="col-3 mb-2">
                        <div className="card border-0" style={{ backgroundColor: 'var(--bg-secondary, #f8f9fa)' }}>
                          <div className="card-body text-center p-2">
                            <div className="text-muted" style={{ fontSize: '0.88rem' }}>{isClosedTrade ? 'Sold Value' : 'Unprotected'}</div>
                            <div className={`fw-bold mb-0 ${isClosedTrade ? 'text-primary' : (remainingShares === 0 ? 'text-success' : 'text-warning')}`} style={{ fontSize: '1.21rem' }}>
                              {isClosedTrade ? formatCurrency((soldPrice || 0) * shares) : remainingShares}
                            </div>
                            {!isClosedTrade && (
                              <div className="text-muted small">
                                of {shares} shares
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                      
                      {/* Row 2 - Shield Impact Info (only when open and shields exist) */}
                      {!isClosedTrade && shields.length > 0 && (
                        <>
                          <div className="col-3 mb-2">
                            <div className="card border-0" style={{ backgroundColor: 'var(--bg-secondary, #f8f9fa)' }}>
                              <div className="card-body text-center p-2">
                                <div className="text-muted" style={{ fontSize: '0.88rem' }}>Total P&L Impact</div>
                                <div className={`fw-bold mb-0 ${totalPotentialPL >= 0 ? 'text-success' : 'text-danger'}`} style={{ fontSize: '1.21rem' }}>
                                  {formatCurrency(totalPotentialPL)}
                                </div>
                              </div>
                            </div>
                          </div>
                          <div className="col-3 mb-2">
                            <div className="card border-0" style={{ backgroundColor: 'var(--bg-secondary, #f8f9fa)' }}>
                              <div className="card-body text-center p-2">
                                <div className="text-muted" style={{ fontSize: '0.88rem' }}>Overall Effect</div>
                                <div className={`fw-bold mb-0 ${overallEffectPercentage >= 0 ? 'text-success' : 'text-danger'}`} style={{ fontSize: '1.21rem' }}>
                                  {formatPercentage(overallEffectPercentage)}
                                </div>
                              </div>
                            </div>
                          </div>
                          <div className="col-3 mb-2">
                            <div className="card border-0" style={{ backgroundColor: 'var(--bg-secondary, #f8f9fa)' }}>
                              <div className="card-body text-center p-2">
                                <div className="text-muted" style={{ fontSize: '0.88rem' }}>Coverage</div>
                                <div className="fw-bold mb-0 text-primary" style={{ fontSize: '1.21rem' }}>
                                  {coveragePercentage.toFixed(1)}%
                                </div>
                              </div>
                            </div>
                          </div>
                          <div className="col-3 mb-2">
                            <div className="card border-0" style={{ backgroundColor: 'var(--bg-secondary, #f8f9fa)' }}>
                              <div className="card-body text-center p-2">
                                <div className="text-muted" style={{ fontSize: '0.88rem' }}>Protection Status</div>
                                <div className="fw-bold mb-0 text-info" style={{ fontSize: '1.21rem' }}>
                                  {shields.length} Level{shields.length > 1 ? 's' : ''}
                                </div>
                              </div>
                            </div>
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                );
              })()}

              {/* Closed-trade injected right panel (e.g., Post Analysis) */}
              {isClosedTrade && closedRightContent && (
                <div className="mt-2 d-flex flex-column flex-grow-1">
                  <div className="flex-grow-1 d-flex flex-column">
                    {closedRightContent}
                  </div>
                </div>
              )}

              {/* Shields List (hidden for closed trades) */}
              {!isClosedTrade && (
                <div className="flex-grow-1 d-flex flex-column">

                {/* Add Shield Form */}
                {renderManualShieldForm()}

                {/* Trailing Summary */}
                {shields.some(shield => shield.shield_type === 'TRAILING_STOP') && (
                  <div className="mb-3 p-2 border rounded d-flex justify-content-between align-items-center" style={{ backgroundColor: 'var(--bg-secondary, #f8f9fa)' }}>
                    <div className="d-flex align-items-center">
                      <span className="text-info me-3">
                        <i className="bi bi-arrow-up-circle me-2"></i>
                        Trailing Summary:
                      </span>
                      {(() => {
                        const trailingStops = shields.filter(shield => shield.shield_type === 'TRAILING_STOP' && shield.trailing_info);
                        if (trailingStops.length === 0) return null;
                        
                        const firstStop = trailingStops[0];
                        return (
                          <>
                            <span className="me-3">
                              Peak ${parseFloat(firstStop.trailing_info.highest_price_seen).toFixed(2)}
                            </span>
                            <span className="text-muted">
                              {getRelativeTime(firstStop.trailing_info.peak_reached_at || firstStop.trailing_info.last_updated)}
                            </span>
                          </>
                        );
                      })()}
                    </div>
                    <Button 
                      variant="outline-info" 
                      size="sm"
                      onClick={loadShields}
                      disabled={loading}
                      title="Refresh trailing stop data"
                    >
                      <i className="bi bi-arrow-clockwise me-1"></i>
                      Refresh
                    </Button>
                  </div>
                )}

                {/* Peak Value Summary for Non-Trailing Stops */}
                {shields.some(shield => shield.shield_type === 'PRICE') && (
                  <div className="mb-3 p-2 border rounded d-flex justify-content-between align-items-center" style={{ backgroundColor: 'var(--bg-secondary, #f8f9fa)' }}>
                    <div className="d-flex align-items-center">
                      {renderPeakSummaryLabel()}
                      {renderPricePeakSummary()}
                    </div>
                    <Button 
                      variant="outline-success" 
                      size="sm"
                      onClick={loadShields}
                      disabled={loading}
                      title="Refresh peak data"
                    >
                      <i className="bi bi-arrow-clockwise me-1"></i>
                      Refresh
                    </Button>
                  </div>
                )}

                {/* Shields Table */}
                {shields.length > 0 && !showAddForm ? (
                  <div className="table-responsive flex-grow-1" style={{ overflowY: 'auto' }}>

                    <table className="table table-sm" style={{ textAlign: 'right' }}>
                      <thead>
                        <tr>
                          <th style={{ textAlign: 'left' }}>
                            Shield Name
                            {(() => {
                              // Calculate available shares for + button
                              if (tickerInfo && !showAddForm) {
                                const totalProtectedShares = shields.reduce((total, shield) => {
                                  return total + (parseFloat(shield.shield_shares) || 0);
                                }, 0);
                                const shares = customShares !== null ? customShares : tickerInfo.shares;
                                const availableShares = Math.max(0, shares - totalProtectedShares);

                                if (availableShares > 0) {
                                  // An overall shield already exists for this ticker+account -- block
                                  // starting a new per-lot stop here so the two mechanisms don't mix.
                                  // Editing an existing per-lot shield (handled by the row's own Edit
                                  // button) is unaffected since it doesn't go through this control.
                                  if (hasOverallShield) {
                                    return (
                                      <Button
                                        variant="outline-secondary"
                                        size="sm"
                                        className="ms-2 rounded-circle"
                                        style={{ width: '24px', height: '24px', padding: '0', cursor: 'not-allowed' }}
                                        disabled
                                        title="Delete the overall stop loss before adding a per-lot stop"
                                      >
                                        <i className="bi bi-plus" style={{ fontSize: '14px' }}></i>
                                      </Button>
                                    );
                                  }
                                  return (
                                    <Button
                                      variant="success"
                                      size="sm"
                                      className="ms-2 rounded-circle"
                                      style={{ width: '24px', height: '24px', padding: '0' }}
                                      onClick={handleShowAddForm}
                                      title={`Add new shield (${availableShares} shares available)`}
                                    >
                                      <i className="bi bi-plus" style={{ fontSize: '14px' }}></i>
                                    </Button>
                                  );
                                }
                              }
                              return null;
                            })()}
                          </th>
                          <th>Price</th>
                          <th>Shares</th>
                          <th>Value</th>
                          <th>From Cost</th>
                          <th>Potential P&L</th>
                          <th style={{ textAlign: 'right' }}>Committed</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {shields.map(shield => {
                          // Calculate how many shares from this specific purchase are protected by this shield
                          const totalShieldShares = parseFloat(shield.shield_shares) || 0;
                          const shieldShares = Math.min(totalShieldShares, shares);
                          
                          // Skip shields that don't protect any shares from this purchase
                          if (shieldShares <= 0) return null;

                          // Recalculate shield values based on custom cost basis
                          const shieldCostBasis = customCostBasis !== null ? customCostBasis : (tickerInfo?.cost_basis || 0);
                          const shieldAmountFromCost = shieldShares * (shield.shield_price - shieldCostBasis);
                          const shieldPercentageFromCost = shieldCostBasis > 0 ? ((shield.shield_price - shieldCostBasis) / shieldCostBasis) * 100 : 0;
                          
                          return (
                            <tr key={shield.shield_id}>
                              <td style={{ textAlign: 'left' }}>
                                <div className="d-flex align-items-center">
                                  <div style={{
                                    width: '8px',
                                    height: '8px',
                                    backgroundColor: getShieldColor(shieldPercentageFromCost, shield, tickerInfo?.current_price),
                                    borderRadius: '50%',
                                    marginRight: '6px'
                                  }}></div>
                                  <div>
                                    <strong>{formatShieldName(shield.shield_name)}</strong>
                                    
                                    {!shield.is_active && <small className="text-muted d-block">Inactive</small>}
                                    {shield.is_active && (tickerInfo?.effective_price || tickerInfo?.current_price) < shield.shield_price && (
                                      <small className="text-danger d-block fw-bold">⚠️ Stop Loss Triggered</small>
                                    )}
                                  </div>
                                </div>
                              </td>
                                                              <td>
                                  <strong className={shield.shield_price >= shieldCostBasis ? 'text-success' : 'text-danger'}>
                                    {formatCurrency(shield.shield_price)}
                                  </strong>
                                </td>
                              <td>
                                <strong>{shieldShares}</strong>
                              </td>
                              <td>
                                <strong>{formatCurrency(shieldShares * shield.shield_price)}</strong>
                              </td>
                              <td>
                                <span className={shieldPercentageFromCost >= 0 ? 'text-success' : 'text-danger'}>
                                  {formatPercentage(shieldPercentageFromCost)}
                                </span>
                              </td>
                              <td>
                                <div className={`fw-bold ${shieldAmountFromCost >= 0 ? 'text-success' : 'text-danger'}`}>
                                  {formatCurrency(shieldAmountFromCost)}
                                </div>
                                <small className="text-muted">
                                  {formatCurrency(shield.shield_price - shieldCostBasis)} per share
                                </small>
                              </td>
                              <td>
                                <div className="text-end">
                                  <Button
                                    variant={shield.is_committed_to_broker ? 'success' : 'warning'}
                                    size="sm"
                                    className="rounded-pill px-2 py-0"
                                    onClick={() => handleShieldCommitToggle(shield)}
                                    disabled={loading || rowCommitLoadingId === shield.shield_id}
                                    title={shield.is_committed_to_broker ? 'Committed' : 'Not Committed'}
                                    aria-label={shield.is_committed_to_broker ? 'Committed' : 'Not Committed'}
                                  >
                                    {rowCommitLoadingId === shield.shield_id ? (
                                      <span className="spinner-border spinner-border-sm" role="status"></span>
                                    ) : (
                                      <>
                                        <i className={`bi ${shield.is_committed_to_broker ? 'bi-check-circle' : 'bi-exclamation-triangle'} me-1`}></i>
                                        {shield.is_committed_to_broker ? 'Yes' : 'No'}
                                      </>
                                    )}
                                  </Button>
                                </div>
                              </td>
                              <td>
                                <div className="d-flex gap-1 justify-content-end">
                                  <Button 
                                    variant="outline-primary" 
                                    size="sm"
                                    onClick={() => handleEditShield(shield)}
                                    disabled={loading}
                                    title="Edit Shield"
                                  >
                                    <i className="bi bi-pencil"></i>
                                  </Button>
                                  <Button 
                                    variant="outline-danger" 
                                    size="sm"
                                    onClick={() => handleDeleteShield(shield.shield_id)}
                                    disabled={loading}
                                    title="Delete Shield"
                                  >
                                    <i className="bi bi-trash"></i>
                                  </Button>
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                      <tfoot>
                        <tr style={{ borderTop: '2px solid var(--border-color, #dee2e6)', backgroundColor: 'var(--bg-secondary, #f8f9fa)' }}>
                          <td style={{ textAlign: 'left' }}><strong>Total Effect</strong></td>
                          <td>
                            {(() => {
                              const relevantShields = shields.filter(shield => {
                                const totalShieldShares = parseFloat(shield.shield_shares) || 0;
                                return Math.min(totalShieldShares, shares) > 0;
                              });
                              const totalProtectedShares = relevantShields.reduce((total, shield) => {
                                const totalShieldShares = parseFloat(shield.shield_shares) || 0;
                                return total + Math.min(totalShieldShares, shares);
                              }, 0);
                              const weightedAvgPrice = totalProtectedShares > 0 ? relevantShields.reduce((total, shield) => {
                                const totalShieldShares = parseFloat(shield.shield_shares) || 0;
                                const shieldShares = Math.min(totalShieldShares, shares);
                                return total + (shield.shield_price * shieldShares);
                              }, 0) / totalProtectedShares : 0;
                              const shieldCostBasis = customCostBasis !== null ? customCostBasis : (tickerInfo?.cost_basis || 0);
                              return (
                                <strong className={weightedAvgPrice >= shieldCostBasis ? 'text-success' : 'text-danger'}>
                                  {formatCurrency(weightedAvgPrice)}
                                </strong>
                              );
                            })()}
                          </td>
                          <td>
                            {(() => {
                              const relevantShields = shields.filter(shield => {
                                const totalShieldShares = parseFloat(shield.shield_shares) || 0;
                                return Math.min(totalShieldShares, shares) > 0;
                              });
                              const totalProtectedShares = relevantShields.reduce((total, shield) => {
                                const totalShieldShares = parseFloat(shield.shield_shares) || 0;
                                return total + Math.min(totalShieldShares, shares);
                              }, 0);
                              return (
                                <strong>{totalProtectedShares}</strong>
                              );
                            })()}
                          </td>
                          <td>
                            {(() => {
                              const relevantShields = shields.filter(shield => {
                                const totalShieldShares = parseFloat(shield.shield_shares) || 0;
                                return Math.min(totalShieldShares, shares) > 0;
                              });
                              const totalValue = relevantShields.reduce((total, shield) => {
                                const totalShieldShares = parseFloat(shield.shield_shares) || 0;
                                const shieldShares = Math.min(totalShieldShares, shares);
                                return total + (shieldShares * shield.shield_price);
                              }, 0);
                              return (
                                <strong>{formatCurrency(totalValue)}</strong>
                              );
                            })()}
                          </td>
                          <td>
                            {(() => {
                              const shieldCostBasis = customCostBasis !== null ? customCostBasis : (tickerInfo?.cost_basis || 0);
                              const relevantShields = shields.filter(shield => {
                                const totalShieldShares = parseFloat(shield.shield_shares) || 0;
                                return Math.min(totalShieldShares, shares) > 0;
                              });
                              const totalPL = relevantShields.reduce((total, shield) => {
                                const totalShieldShares = parseFloat(shield.shield_shares) || 0;
                                const shieldShares = Math.min(totalShieldShares, shares);
                                return total + (shieldShares * (shield.shield_price - shieldCostBasis));
                              }, 0);
                              const costBasisPositionValue = (shieldCostBasis || 0) * shares;
                              const overallEffectPercentage = costBasisPositionValue > 0 ? (totalPL / costBasisPositionValue) * 100 : 0;
                              return (
                                <strong className={overallEffectPercentage >= 0 ? 'text-success' : 'text-danger'}>
                                  {formatPercentage(overallEffectPercentage)}
                                </strong>
                              );
                            })()}
                          </td>
                          <td>
                            {(() => {
                              const shieldCostBasis = customCostBasis !== null ? customCostBasis : (tickerInfo?.cost_basis || 0);
                              const relevantShields = shields.filter(shield => {
                                const totalShieldShares = parseFloat(shield.shield_shares) || 0;
                                return Math.min(totalShieldShares, shares) > 0;
                              });
                              const totalPL = relevantShields.reduce((total, shield) => {
                                const totalShieldShares = parseFloat(shield.shield_shares) || 0;
                                const shieldShares = Math.min(totalShieldShares, shares);
                                return total + (shieldShares * (shield.shield_price - shieldCostBasis));
                              }, 0);
                              return (
                                <div className={`fw-bold ${totalPL >= 0 ? 'text-success' : 'text-danger'}`}>
                                  {formatCurrency(totalPL)}
                                </div>
                              );
                            })()}
                          </td>
                          <td className="text-end">
                            <Button
                              variant={shields.every(s => s.is_committed_to_broker) ? 'success' : 'warning'}
                              size="sm"
                              className="rounded-pill px-2 py-0"
                              disabled={loading || shields.length === 0}
                              onClick={() => handleGlobalCommitToggle()}
                              title={shields.every(s => s.is_committed_to_broker) ? 'All Committed' : 'Not All Committed'}>
                              <i className={`bi ${shields.every(s => s.is_committed_to_broker) ? 'bi-check-circle' : 'bi-exclamation-triangle'} me-1`}></i>
                              {shields.every(s => s.is_committed_to_broker) ? 'Yes' : 'No'}
                            </Button>
                          </td>
                          <td className="text-end">
                            <Button
                              variant="outline-danger"
                              size="sm"
                              disabled={loading || shields.length === 0}
                              onClick={handleDeleteAllShields}
                              title="Delete all stop losses">
                              <i className="bi bi-trash"></i>
                            </Button>
                          </td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                ) : (
                  // Block starting a *new* per-lot stop while an overall shield exists (mirrors
                  // the "+" button's gate above), but still let an in-progress edit of an
                  // existing per-lot shield (editingShield set) through.
                  hasOverallShield && !editingShield ? (
                    <div className="text-center text-muted small p-4 border rounded">
                      <i className="bi bi-lock-fill me-1"></i>
                      An overall stop loss is already set for this ticker. Delete it before adding a per-lot stop here.
                    </div>
                  ) : (
                    renderStopCreationForm()
                  )
                )}

                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      {renderSizeSelectionModal()}
      </>
    );
  }
  // Default: show icon and modal as before
  return (
    <>
      {renderShieldIcon()}
      <Modal show={showModal} onHide={handleClose} size="xl" className="shield-modal">
        <Modal.Header closeButton style={{ paddingRight: '2rem' }} className="shield-modal-header">
          <div className="d-flex align-items-center justify-content-between gap-3 flex-wrap w-100 pe-4">
            <Modal.Title style={{ fontSize: '2rem' }}>
              Shields - {tickerInfo?.symbol}
              {accountInfo?.name && (
                <span style={{ fontSize: '1.2rem', color: 'var(--bs-secondary, #6c757d)', marginLeft: '0.5rem' }}>
                  ({accountInfo.name})
                </span>
              )}
            </Modal.Title>
            <div className="d-flex align-items-center gap-2">
              <Form.Label className="mb-0 text-nowrap" style={{ fontSize: '0.95rem', fontWeight: 600 }}>
                New Position Default
              </Form.Label>
              <Form.Select
                size="sm"
                value={defaultNewPositionShieldPreset}
                onChange={handleDefaultNewPositionShieldPresetChange}
                disabled={defaultPresetSaving || !accountId}
                style={{ minWidth: '180px' }}
                aria-label="Default shield preset for new positions"
              >
                {DEFAULT_NEW_POSITION_SHIELD_PRESET_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </Form.Select>
            </div>
          </div>
        </Modal.Header>
        <Modal.Body>
          <style>
            {`
              .shield-modal .modal-dialog {
                max-width: 1300px !important;
                width: 100% !important;
                margin: 1.75rem auto;
              }
              
              .shield-modal .modal-content {
                width: 100%;
                height: 85vh;
                max-height: 85vh;
              }
              
              .shield-modal .modal-body {
                height: calc(85vh - 120px);
                overflow-y: auto;
                padding: 1.5rem;
              }
              
              @media (prefers-color-scheme: dark) {
                .shield-modal-header .btn-close {
                  filter: brightness(0) invert(1) !important;
                  background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 16' fill='%23fff'%3e%3cpath d='M.293.293a1 1 0 0 1 1.414 0L8 6.586 14.293.293a1 1 0 1 1 1.414 1.414L9.414 8l6.293 6.293a1 1 0 0 1-1.414 1.414L8 9.414l-6.293 6.293a1 1 0 0 1-1.414-1.414L6.586 8 .293 1.707a1 1 0 0 1 0-1.414z'/%3e%3c/svg%3e") !important;
                }
              }
              
              [data-bs-theme="dark"] .shield-modal-header .btn-close {
                filter: brightness(0) invert(1) !important;
                background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 16' fill='%23fff'%3e%3cpath d='M.293.293a1 1 0 0 1 1.414 0L8 6.586 14.293.293a1 1 0 1 1 1.414 1.414L9.414 8l6.293 6.293a1 1 0 0 1-1.414 1.414L8 9.414l-6.293 6.293a1 1 0 0 1-1.414-1.414L6.586 8 .293 1.707a1 1 0 0 1 0-1.414z'/%3e%3c/svg%3e") !important;
              }
              
              .shield-modal .shield-modal-header .btn-close {
                filter: brightness(0) invert(1) !important;
                background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 16' fill='%23fff'%3e%3cpath d='M.293.293a1 1 0 0 1 1.414 0L8 6.586 14.293.293a1 1 0 1 1 1.414 1.414L9.414 8l6.293 6.293a1 1 0 0 1-1.414 1.414L8 9.414l-6.293 6.293a1 1 0 0 1-1.414-1.414L6.586 8 .293 1.707a1 1 0 0 1 0-1.414z'/%3e%3c/svg%3e") !important;
              }
              
              /* Custom column widths for 10% reduction in visual section */
              .shield-modal .col-md-3 {
                flex: 0 0 27% !important;
                max-width: 27% !important;
              }
              .shield-modal .col-md-9 {
                flex: 0 0 73% !important;
                max-width: 73% !important;
              }
            `}
          </style>
          {error && <Alert variant="danger">{error}</Alert>}
          
          {loading && (
            <div className="text-center">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
            </div>
          )}

          {/* Main Layout: Visualization (Left) + Shields Management (Right) */}
          <div className="d-flex flex-column h-100 armor-edit-expanded">
            <div className="row flex-grow-1">
              {/* Left Column - Shield Visualization */}
              <div className="col-md-3 d-flex flex-column">
                {renderShieldVisualization()}
              </div>

              {/* Right Column - Stats + Shield Summary + Shields Management */}
              <div className="col-md-9 d-flex flex-column">
                {/* Combined Portfolio & Shield Statistics */}
                {tickerInfo && (() => {
                  // Calculate remaining shares for protection
                  const totalProtectedShares = shields.reduce((total, shield) => {
                    return total + (parseFloat(shield.shield_shares) || 0);
                  }, 0);
                  const remainingShares = Math.max(0, tickerInfo.shares - totalProtectedShares);
                  
                  // Shield calculations
                  const totalPotentialPL = shields.reduce((total, shield) => {
                    return total + (shield.amount_from_cost || 0);
                  }, 0);
                  const coveragePercentage = totalProtectedShares > 0 ? (totalProtectedShares / tickerInfo.shares) * 100 : 0;
                  // Overall shields effect as percentage of cost basis position value
                  const costBasisPositionValue = tickerInfo.cost_basis * tickerInfo.shares;
                  const overallEffectPercentage = costBasisPositionValue > 0 ? (totalPotentialPL / costBasisPositionValue) * 100 : 0;
                  
                  return (
                    <div className="mb-2">
                      {(() => {
                        const costBasis = tickerInfo.cost_basis * tickerInfo.shares;
                        const currentValue = (tickerInfo.effective_price || tickerInfo.current_price) * tickerInfo.shares;
                        const totalGain = currentValue - costBasis;
                        const totalGainPct = costBasis > 0 ? (totalGain / costBasis) * 100 : 0;
                        
                        return (
                          <div className="mb-3 text-center">
                            <span className="me-4">
                              <span className="text-muted">Cost Basis: </span>
                              <span className="fw-bold text-secondary">{formatCurrency(costBasis)}</span>
                            </span>
                            <span className="me-4">
                              <span className="text-muted">Current Value: </span>
                              <span className="fw-bold text-primary">{formatCurrency(currentValue)}</span>
                            </span>
                            <span>
                              <span className="text-muted">Total Gain: </span>
                              <span className={`fw-bold ${totalGain >= 0 ? 'text-success' : 'text-danger'}`}>
                                {formatCurrency(totalGain)} ({formatPercentage(totalGainPct)})
                              </span>
                            </span>
                          </div>
                        );
                      })()}
                      <div className="row">
                        {/* Row 1 - Basic Portfolio Info */}
                        <div className="col-3 mb-2">
                          <div className="card border-0" style={{ backgroundColor: 'var(--bg-secondary, #f8f9fa)' }}>
                            <div className="card-body text-center p-2">
                              <div className="text-muted" style={{ fontSize: '0.88rem' }}>
                                {tickerInfo.manual_price ? 'Manual Price' : 'Current Price'}
                              </div>
                              <div className="fw-bold text-primary mb-0" style={{ fontSize: '1.21rem' }}>
                                {formatCurrency(tickerInfo.effective_price || tickerInfo.current_price)}
                                {tickerInfo.manual_price && (
                                  <span className="text-info ms-1" title="Manual price is set">📝</span>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                        <div className="col-3 mb-2">
                          <div className="card border-0" style={{ backgroundColor: 'var(--bg-secondary, #f8f9fa)' }}>
                            <div className="card-body text-center p-2">
                              <div className="text-muted" style={{ fontSize: '0.88rem' }}>Average Cost</div>
                              <div className="fw-bold text-secondary mb-0" style={{ fontSize: '1.21rem' }}>
                                {formatCurrency(tickerInfo.cost_basis)}
                              </div>
                            </div>
                          </div>
                        </div>
                        <div className="col-3 mb-2">
                          <div className="card border-0" style={{ backgroundColor: 'var(--bg-secondary, #f8f9fa)' }}>
                            <div className="card-body text-center p-2">
                              <div className="text-muted" style={{ fontSize: '0.88rem' }}>Total Shares</div>
                              <div className="fw-bold mb-0" style={{ fontSize: '1.21rem' }}>
                                {tickerInfo.shares}
                              </div>
                            </div>
                          </div>
                        </div>
                        <div className="col-3 mb-2">
                          <div className="card border-0" style={{ backgroundColor: 'var(--bg-secondary, #f8f9fa)' }}>
                            <div className="card-body text-center p-2">
                              <div className="text-muted" style={{ fontSize: '0.88rem' }}>Unprotected</div>
                              <div className={`fw-bold mb-0 ${remainingShares === 0 ? 'text-success' : 'text-warning'}`} style={{ fontSize: '1.21rem' }}>
                                {remainingShares}
                              </div>
                            </div>
                          </div>
                        </div>
                        
                        {/* Row 2 - Shield Impact Info (only show if shields exist) */}
                        {shields.length > 0 && (
                          <>
                            <div className="col-3 mb-2">
                              <div className="card border-0" style={{ backgroundColor: 'var(--bg-secondary, #f8f9fa)' }}>
                                <div className="card-body text-center p-2">
                                  <div className="text-muted" style={{ fontSize: '0.88rem' }}>Total P&L Impact</div>
                                  <div className={`fw-bold mb-0 ${totalPotentialPL >= 0 ? 'text-success' : 'text-danger'}`} style={{ fontSize: '1.21rem' }}>
                                    {formatCurrency(totalPotentialPL)}
                                  </div>
                                </div>
                              </div>
                            </div>
                            <div className="col-3 mb-2">
                              <div className="card border-0" style={{ backgroundColor: 'var(--bg-secondary, #f8f9fa)' }}>
                                <div className="card-body text-center p-2">
                                  <div className="text-muted" style={{ fontSize: '0.88rem' }}>Overall Effect</div>
                                  <div className={`fw-bold mb-0 ${overallEffectPercentage >= 0 ? 'text-success' : 'text-danger'}`} style={{ fontSize: '1.21rem' }}>
                                    {formatPercentage(overallEffectPercentage)}
                                  </div>
                                </div>
                              </div>
                            </div>
                            <div className="col-3 mb-2">
                              <div className="card border-0" style={{ backgroundColor: 'var(--bg-secondary, #f8f9fa)' }}>
                                <div className="card-body text-center p-2">
                                  <div className="text-muted" style={{ fontSize: '0.88rem' }}>Coverage</div>
                                  <div className="fw-bold mb-0 text-primary" style={{ fontSize: '1.21rem' }}>
                                    {coveragePercentage.toFixed(1)}%
                                  </div>
                                </div>
                              </div>
                            </div>
                            <div className="col-3 mb-2">
                              <div className="card border-0" style={{ backgroundColor: 'var(--bg-secondary, #f8f9fa)' }}>
                                <div className="card-body text-center p-2">
                                  <div className="text-muted" style={{ fontSize: '0.88rem' }}>Protection Status</div>
                                  <div className="fw-bold mb-0 text-info" style={{ fontSize: '1.21rem' }}>
                                    {shields.length} Level{shields.length > 1 ? 's' : ''}
                                  </div>
                                </div>
                              </div>
                            </div>
                          </>
                        )}
                      </div>
                    </div>
                  );
                })()}

                <>
                {/* Shields List */}
                <div className="flex-grow-1 d-flex flex-column">
                  {/* Stops Mode Toggle: whole-position average cost vs. per-lot (Trade Details) */}
                  <div className="d-flex align-items-center justify-content-center mb-3 p-2 border rounded" style={{
                    backgroundColor: 'var(--card-bg, white)',
                    borderColor: 'var(--border-color, #dee2e6)'
                  }}>
                    <div className="btn-group" role="group">
                      <input
                        type="radio"
                        className="btn-check"
                        name={`${instanceId}-stopsMode`}
                        id={`${instanceId}-overallStops`}
                        checked={stopsMode === 'overall'}
                        disabled={hasPerLotShield}
                        onChange={() => {
                          setStopsMode('overall');
                          setShowAddForm(false);
                          setEditingShield(null);
                        }}
                      />
                      <label
                        className="btn btn-outline-primary"
                        htmlFor={`${instanceId}-overallStops`}
                        title={hasPerLotShield ? 'Delete the per-lot stops to switch to an overall stop' : undefined}
                      >
                        Average Cost Based Overall Stops
                      </label>

                      <input
                        type="radio"
                        className="btn-check"
                        name={`${instanceId}-stopsMode`}
                        id={`${instanceId}-individualStops`}
                        checked={stopsMode === 'individual'}
                        disabled={hasOverallShield}
                        onChange={() => {
                          setStopsMode('individual');
                          setShowAddForm(false);
                          setEditingShield(null);
                        }}
                      />
                      <label
                        className="btn btn-outline-primary"
                        htmlFor={`${instanceId}-individualStops`}
                        title={hasOverallShield ? 'Delete the overall stop loss to switch to per-lot stops' : undefined}
                      >
                        Individual Buy Slot Based Stops
                      </label>
                    </div>
                  </div>
                </div>

                  {/* Trailing Summary */}
                  {shields.some(shield => shield.shield_type === 'TRAILING_STOP') && (
                    <div className="mb-3 p-2 border rounded d-flex justify-content-between align-items-center" style={{ backgroundColor: 'var(--bg-secondary, #f8f9fa)' }}>
                      <div className="d-flex align-items-center">
                        <span className="text-info me-3">
                          <i className="bi bi-arrow-up-circle me-2"></i>
                          Trailing Summary:
                        </span>
                        {(() => {
                          const trailingStops = shields.filter(shield => shield.shield_type === 'TRAILING_STOP' && shield.trailing_info);
                          if (trailingStops.length === 0) return null;
                          
                          const firstStop = trailingStops[0];
                          return (
                            <>
                              <span className="me-3">
                                Peak ${parseFloat(firstStop.trailing_info.highest_price_seen).toFixed(2)}
                              </span>
                              <span className="text-muted">
                                {getRelativeTime(firstStop.trailing_info.peak_reached_at || firstStop.trailing_info.last_updated)}
                              </span>
                            </>
                          );
                        })()}
                      </div>
                      <Button 
                        variant="outline-info" 
                        size="sm"
                        onClick={loadShields}
                        disabled={loading}
                        title="Refresh trailing stop data"
                      >
                        <i className="bi bi-arrow-clockwise me-1"></i>
                        Refresh
                      </Button>
                    </div>
                  )}

                  {/* Peak Value Summary for Non-Trailing Stops */}
                  {shields.some(shield => shield.shield_type === 'PRICE') && (
                    <div className="mb-3 p-2 border rounded d-flex justify-content-between align-items-center" style={{ backgroundColor: 'var(--bg-secondary, #f8f9fa)' }}>
                      <div className="d-flex align-items-center">
                        {renderPeakSummaryLabel()}
                        {renderPricePeakSummary()}
                      </div>
                      <Button 
                        variant="outline-success" 
                        size="sm"
                        onClick={loadShields}
                        disabled={loading}
                        title="Refresh peak data"
                      >
                        <i className="bi bi-arrow-clockwise me-1"></i>
                        Refresh
                      </Button>
                    </div>
                  )}

                  {/* Shields Table / Stop Creation, branched by Stops Mode */}
                  {(() => {
                    const totalProtectedShares = shields.reduce((total, shield) => total + (parseFloat(shield.shield_shares) || 0), 0);
                    const remainingShares = Math.max(0, (tickerInfo?.shares || 0) - totalProtectedShares);

                    if (stopsMode === 'individual') {
                      // Unchanged pre-existing behavior: browse existing shields here,
                      // but new ones are only set up per-lot from Trade Details.
                      return shields.length > 0 ? (
                        <>
                          {renderAggregateShieldsTable()}
                          <div className="d-flex justify-content-center my-3">
                            {renderAddStopCircleButton(
                              remainingShares <= 0 || hasOverallShield,
                              hasOverallShield ? 'Delete the overall stop loss before adding per-lot stops' : undefined
                            )}
                          </div>
                        </>
                      ) : (
                        <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '40vh' }}>
                          {renderAddStopCircleButton((tickerInfo?.shares || 0) <= 0)}
                        </div>
                      );
                    }

                    // 'overall' mode: create/manage stops right here against the
                    // ticker's average cost basis, without leaving the modal. Blocked
                    // while a per-lot shield exists (mirrors the 'individual' branch's
                    // hasOverallShield gate above), except to finish an in-progress edit
                    // of an existing overall shield (editingShield set).
                    if (showAddForm) {
                      if (hasPerLotShield && !editingShield) {
                        return (
                          <div className="text-center text-muted small p-4 border rounded">
                            <i className="bi bi-lock-fill me-1"></i>
                            Per-lot stops are already set for this ticker. Delete them before adding an overall stop.
                          </div>
                        );
                      }
                      return (
                        <>
                          {renderManualShieldForm()}
                          {renderStopCreationForm()}
                        </>
                      );
                    }

                    return shields.length > 0 ? (
                      <>
                        {renderAggregateShieldsTable(true)}
                        <div className="d-flex justify-content-center my-3">
                          {renderAddOverallStopButton(
                            remainingShares <= 0 || hasPerLotShield,
                            hasPerLotShield ? 'Delete the per-lot stops before adding an overall stop' : undefined
                          )}
                        </div>
                      </>
                    ) : (
                      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '40vh' }}>
                        {renderAddOverallStopButton(
                          (tickerInfo?.shares || 0) <= 0 || hasPerLotShield,
                          hasPerLotShield ? 'Delete the per-lot stops before adding an overall stop' : undefined
                        )}
                      </div>
                    );
                  })()}

                </>
              </div>
            </div>
          </div>
        </Modal.Body>
      </Modal>

      {/* Size/Distribution Selection Modal */}
      {renderSizeSelectionModal()}
    </>
  );
}

// Add CSS for spinning animation
const style = document.createElement('style');
style.textContent = `
  .spin {
    animation: spin 1s linear infinite;
  }
  
  @keyframes spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }
`;
document.head.appendChild(style);

export default ArmorEdit; 
